import { useCallback, useMemo, useRef, useState } from "react";
import {
  ActivityIndicator,
  AppState,
  FlatList,
  Platform,
  Pressable,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
  useWindowDimensions,
} from "react-native";

import { Modal } from "@/components/ui/app-modal";
import Ionicons from "@expo/vector-icons/Ionicons";
import { useFocusEffect, useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { SafeAreaView } from "react-native-safe-area-context";
import { useTranslation } from "react-i18next";

import { groupApi } from "@/api";
import { EditLocationEditor } from "@/components/edit-location-editor";
import { NearbyRow } from "@/components/nearby-row";
import { ageBandRangeLabel, parseBandRangeLabel } from "@/lib/age-bands";
import { BlurredBackdrop } from "@/components/ui/blurred-backdrop";
import { CompactSwitch } from "@/components/ui/compact-switch";
import { KeyboardAvoidingContainer } from "@/components/ui/keyboard-view";
import { Loading } from "@/components/ui/loading";
import { FRIENDSHIP_GOALS, LANGUAGE_OPTIONS } from "@/constants/options";
import { cueCooldownFrom, cueCooldownMessage } from "@/lib/cue-cooldown";
import { notify } from "@/lib/dialog";
import { shareText } from "@/lib/share-text";
import { formatCompactDuration } from "@/lib/duration";
import {
  getCurrentCoords,
  getLocationPrecision,
  requestPreciseLocation,
  type LocationPrecision,
} from "@/lib/location";
import {
  getLocationAccessSteps,
  getWebEnvironment,
  openAppSettings,
  openLocationSettings,
  requestLocationAccess,
  type LocationAccess,
} from "@/lib/location-access";
import { locationRetryAction } from "@/lib/location-retry";
import { rememberGroupHeader } from "@/lib/group-cache";
import { groupCreateCacheBoundMs } from "@/lib/group-create-coords";
import { locationStrings } from "@/i18n/features/location.i18n";
import { useLocationAccessStore } from "@/state/location-access-store";
import i18n from "@/i18n";
import { groupGoalKeys, homeStrings } from "@/i18n/features/home.i18n";
import { isLive } from "@/lib/visibility-intent";
import { useAuthStore } from "@/state/auth-store";
import {
  effectiveNearbyMode,
  useSessionStore,
} from "@/state/session-store";
import type { NearbyError, NearbyMode } from "@/state/session-store";
import { useAppTheme } from "@/theme/app-theme";
import { AgeRangeSlider } from "@/components/age-range-slider";
import { NearbyModeToggle } from "@/components/nearby-mode-toggle";
import { NearbyRadiusSlider } from "@/components/nearby-radius-slider";
import { handleFor } from "@/lib/profile-handle";
import {
  ANY_AGE_RANGE_LABEL,
} from "@/lib/age-range";
import {
  NEARBY_RADIUS_DEFAULT_M,
  clampNearbyRadiusM,
  formatRadiusM,
} from "@/lib/nearby-radius";
import type {
  CreateSmallGroupInput,
  GroupBrowseFilters,
  PeopleFilterPreferences,
  NearbyUser,
  SmallGroup,
  User,
} from "@/types";

const FONT_FAMILY = Platform.select({
  ios: "Avenir Next",
  android: "sans-serif",
  default: undefined,
});

const BRAND_BLUE = "#1a335a";
const BRAND_BLUE_SHADOW = "#1E58B9";
const CONTENT_MAX_WIDTH = 680;
const CONTENT_SIDE_PADDING = 22;
const PEOPLE_PANEL_MAX_WIDTH = CONTENT_MAX_WIDTH - CONTENT_SIDE_PADDING * 2;
/**
 * Space under the people panel, above the tab bar.
 *
 * Was 40, which was pure decoration: the tab bar is a laid-out element rather
 * than an absolutely-positioned overlay (`(tabs)/_layout.tsx` sets a height and
 * no `position`), so the panel was never at risk of sliding underneath it and
 * this gap bought nothing but 40px of empty screen — on the surface whose whole
 * job is fitting people onto it.
 *
 * The panel keeps its own inner `paddingBottom`, so the last row still has room
 * to breathe inside the card; what went is the gap outside it.
 */
const HOME_FOOTER_GAP = 0;
const GROUP_GOALS = [
  "Casual Hello",
  "Coffee Chat",
  "Work/Study Break",
  "Group Hangout",
  "Try Something New",
  "Explore Nearby",
  "Food & Coffee",
  "Walk/Fitness",
  "Local Event",
  "Low-Pressure Chat",
  "Other",
];
const OTHER_GROUP_GOAL = "Other";
const CUSTOM_GROUP_GOAL_MAX_LENGTH = 25;
const GROUP_SIZES = [3, 4, 5, 6];
// How long a Small Group stays open before it expires, in minutes: 3h, 9h, 16h,
// 24h, 48h. Kept in sync with GROUP_DURATION_MINUTES on the backend, which turns
// the chosen value into the group's real expires_at.
const GROUP_DURATIONS = [180, 540, 960, 1440, 2880];
const DEFAULT_GROUP_DURATION = 180;

// Age-range bounds, parsing and formatting live in `lib/age-range.ts`.
// 5 km unless the user moves the slider. Starting wide is what makes an empty
// room still show somebody — narrowing is an opt-in, not a starting point. It
// is the CENTRE anchor rather than the widest since the ceiling went to 10 km:
// 10 km is four times the area for a list capped at the same 75 people. The
// default and bounds live in `lib/nearby-radius.ts`, mirroring the server's
// `common/nearby-radius.ts`.

// Add your website, App Store, or Play Store link here.
// One stable URL, routed at the edge: the landing worker's /get sends
// Android to the Play listing, iPhones to the App Store, desktops to the
// web app. Where it points can change with a landing deploy — never an app
// update — which is why the app carries this one address and no store links.
const OPNCUE_SHARE_URL = "https://opncue.com/get";

function getHomeColors(isDark: boolean) {
  return {
    screen: isDark ? "#0D1724" : "#FCFDFF",
    topBar: isDark ? "#101C2C" : "#FCFDFF",

    surface: isDark ? "#152235" : "#FFFFFF",
    surfaceMuted: isDark ? "#19283B" : "#FAFBFD",
    surfaceSoft: isDark ? "#1B2C43" : "#F4F8FF",
    surfacePressed: isDark ? "#213650" : "#F4F8FF",

    textPrimary: isDark ? "#EFF5FF" : "#102D51",
    textStrong: isDark ? "#DFEAFA" : "#153252",
    textBody: isDark ? "#C0CDDD" : "#425873",
    textSecondary: isDark ? "#A8B6C9" : "#7B879A",
    textMuted: isDark ? "#8999AD" : "#929DAC",

    border: isDark ? "#2C3C51" : "#E1E8F1",
    borderSoft: isDark ? "#29394D" : "#E4EAF2",
    divider: isDark ? "#2A3A4E" : "#E3E9F1",

    primary: isDark ? "#6EA8FF" : "#2468D8",
    primaryStrong: isDark ? "#91BCFF" : "#2463CB",
    primaryText: isDark ? "#B9D3FF" : "#3568B7",
    primarySoft: isDark ? "#203754" : "#F0F5FD",
    primarySurface: isDark ? "#1D314B" : "#F3F7FF",
    primaryBorder: isDark ? "#34577F" : "#DDE7F7",

    iconNeutral: isDark ? "#A9B9CD" : "#526C8D",

    green: isDark ? "#57C98D" : "#35A86C",
    greenSoft: isDark ? "#19392E" : "#F1FAF5",
    greenBorder: isDark ? "#2F634C" : "#D7EADF",

    danger: isDark ? "#FF929B" : "#A64A54",
    dangerSoft: isDark ? "#3A222B" : "#FFF3F4",

    shadow: isDark ? "#000000" : "#7187A6",

    white: "#FFFFFF",
  };
}

type HomeColors = ReturnType<typeof getHomeColors>;

type HomeStyles = ReturnType<typeof createStyles>;

type HomePanel = "people" | "groups";

/**
 * Deliberately does not include gender, religion or politics. Those are not
 * things a stranger gets to sort the room by: filtering on them requires the
 * server to disclose them to everyone in range, and the disclosure is the
 * problem, not the UI. Religion and politics are private attributes outright.
 */
type PeopleFilters = {
  ageBand: string | null;
  language: string | null;
  friendshipGoal: string | null;
};

type PendingCueTarget = {
  id: string;
  name: string;
};

type PendingGroupIntent = "create" | { action: "join"; group: SmallGroup };

const EMPTY_PEOPLE_FILTERS: PeopleFilters = {
  ageBand: null,
  language: null,
  friendshipGoal: null,
};

type FilterLocks = Record<keyof PeopleFilters, string | null>;

const FILTER_OPTION_LISTS = {
  languages: LANGUAGE_OPTIONS,
  friendshipGoals: FRIENDSHIP_GOALS,
};

function goalLabel(value: string): string {
  return FRIENDSHIP_GOALS.find((goal) => goal.value === value)?.label ?? value;
}

function languageLabel(value: string): string {
  return (
    LANGUAGE_OPTIONS.find((language) => language.value === value)?.label ??
    value
  );
}

function profileFilterLocks(user: User | null): FilterLocks {
  return {
    ageBand:
      user?.ageBand || user?.dateOfBirth || user?.age
        ? null
        : i18n.t(homeStrings.profileAgeRequired),
    language: user?.languages?.length
      ? null
      : i18n.t(homeStrings.profileLanguagesRequired),
    friendshipGoal: user?.lookingFor?.length
      ? null
      : i18n.t(homeStrings.profileGoalRequired),
  };
}

function groupGoalLabel(value: string): string {
  const key = groupGoalKeys[value as keyof typeof groupGoalKeys];
  return key ? i18n.t(key) : value;
}

function usableFilters(
  filters: PeopleFilters,
  locks: FilterLocks,
): PeopleFilters {
  return {
    ageBand: locks.ageBand ? null : filters.ageBand,
    language: locks.language ? null : filters.language,
    friendshipGoal: locks.friendshipGoal ? null : filters.friendshipGoal,
  };
}





function filterCount(
  filters: PeopleFilters,
  nearbyRadiusM: number,
  sameSpace: boolean,
): number {
  // Anything narrower than the default counts as one active filter.
  const profileRangeCount = nearbyRadiusM < NEARBY_RADIUS_DEFAULT_M ? 1 : 0;
  // Same Space is the NARROWEST filter there is — it replaces "everyone in
  // range" with "the people who joined this one room" — and it was the only
  // one invisible from the list itself. The badge is how somebody remembers,
  // a day later, why the screen looks empty; without it the feature is a
  // setting you can forget you are wearing.
  //
  // It also replaces the range rather than adding to it, so the two are never
  // counted together: the sheet hides the slider in this mode, and counting a
  // range that is not being applied would be a badge for a filter that is not
  // running.
  if (sameSpace) return Object.values(filters).filter(Boolean).length + 1;
  return Object.values(filters).filter(Boolean).length + profileRangeCount;
}

type GroupFilters = {
  vibe: string | null;
  ageRange: string | null;
  size: string | null;
};

const EMPTY_GROUP_FILTERS: GroupFilters = {
  vibe: null,
  ageRange: null,
  size: null,
};

type HomeListItem =
  | { type: "header" }
  | { type: "tabs" }
  | { type: "person"; person: NearbyUser }
  | { type: "peopleLoading" }
  | { type: "peopleEmpty" }
  | { type: "peoplePreciseRequired" }
  | { type: "groups" };

/**
 * The viewer's group filters, in the shape the API takes.
 *
 * This replaced `filterGroups`, which ran over the already-capped response —
 * and which tested age by OVERLAP, so an unrestricted group matched every
 * filter and asking for 20-30 also returned the 20-50 group. The server tests
 * CONTAINMENT: a group qualifies only if its whole declared range fits inside
 * the request, which is what "I only want groups aimed at 20-30" means.
 *
 * The age filter is a preference and is separate from eligibility, which the
 * server applies regardless — a 20-50 group will accept a 30-year-old, and this
 * is how they say they do not want it.
 */
/**
 * The saved filters, in the shape this screen's controls speak.
 *
 * The controls work in labels ("25-34") because that is what a person reads;
 * the server stores band FLOORS because that is what a query can evaluate.
 * Converting at this one boundary keeps both honest — and the label is always
 * rebuilt from the floors rather than remembered, so what the sheet shows is
 * what the server is applying.
 */
function peopleFiltersFromSaved(
  saved: PeopleFilterPreferences | null | undefined,
): PeopleFilters {
  if (!saved) return EMPTY_PEOPLE_FILTERS;
  return {
    ageBand:
      saved.ageBandMinFloor !== null && saved.ageBandMaxFloor !== null
        ? ageBandRangeLabel({
            minFloor: saved.ageBandMinFloor,
            maxFloor: saved.ageBandMaxFloor,
          })
        : null,
    language: saved.language,
    friendshipGoal: saved.friendshipGoal,
  };
}

function savedFromPeopleFilters(
  filters: PeopleFilters,
): PeopleFilterPreferences | null {
  const range = parseBandRangeLabel(filters.ageBand);
  const saved: PeopleFilterPreferences = {
    ageBandMinFloor: range?.minFloor ?? null,
    ageBandMaxFloor: range?.maxFloor ?? null,
    language: filters.language,
    friendshipGoal: filters.friendshipGoal,
  };
  // Nothing chosen is no filter at all, not an empty one — the server stores
  // null for it either way, and sending the empty object would write on every
  // open-and-close of the sheet.
  return saved.ageBandMinFloor === null &&
    saved.language === null &&
    saved.friendshipGoal === null
    ? null
    : saved;
}

function groupBrowseFilters(filters: GroupFilters): GroupBrowseFilters {
  const range = parseBandRangeLabel(filters.ageRange);
  return {
    ...(filters.vibe ? { vibe: filters.vibe } : {}),
    ...(filters.size ? { maxMembers: Number(filters.size) } : {}),
    ...(range
      ? {
          ageBandMinFloor: range.minFloor,
          ageBandMaxFloor: range.maxFloor,
        }
      : {}),
  };
}

function groupFilterCount(filters: GroupFilters): number {
  return Object.values(filters).filter(Boolean).length;
}

/**
 * How stale the people list may be before a focus re-reads it.
 *
 * Not a poll: nothing fires while the screen simply sits there. This is the
 * bound on how old the answer is allowed to be at the moment the user looks at
 * it again.
 *
 * Two minutes, and the ceiling above it is the server's own: presence liveness
 * is a Redis key with a 300s TTL (`PRESENCE_LIVENESS_TTL_S`), so a device that
 * dies silently keeps being answered as present for up to five minutes however
 * often anybody asks. A client bound much tighter than that spends requests
 * re-reading an answer that cannot have changed yet.
 */
const NEARBY_REFOCUS_MAX_AGE_MS = 120_000;

/**
 * The same bound for a list somebody has paged through.
 *
 * A re-read costs them more: `refreshNearby` is the first page by definition,
 * so it rewinds 150 rows to 75 and loses their place. That buys the shorter
 * bound a real exception — but not an unlimited one, which is what skipping
 * the re-read outright amounted to. A paged list could then carry a departed
 * row for as long as the app stayed open, and "these people are here now" is
 * the one thing this screen claims.
 *
 * So the exception is a longer window, not an absent one: ten minutes, by
 * which point the rows are two liveness TTLs old and the rewind is the lesser
 * loss. Everything that converges the list sooner still applies — presence
 * events, the resync gate, pull-to-refresh — this is only the floor under
 * them.
 */
const NEARBY_PAGED_REFOCUS_MAX_AGE_MS = 600_000;

/**
 * How often the reconciliation below ASKS. What it actually reads is decided
 * by the two windows above, so this is a polling grain, not a refresh rate.
 *
 * Sixty seconds against a 120s window means an unpaged list reads on every
 * second tick; against 600s a paged one reads on every tenth. The ticks in
 * between cost a `Map` lookup in the launch gate and nothing else. Finer than
 * this buys no freshness — the windows would still refuse — and coarser makes
 * the effective cadence lumpy, because a tick that arrives at 119s waits
 * another full minute for a window that expired one second ago.
 *
 * It is also the retry interval when a read FAILS, and that is deliberate: a
 * failed background read no longer stamps freshness (`launch-refresh.ts`), so
 * the next tick tries again rather than inheriting a window it never earned.
 * One attempt per minute per client is the floor that puts under a backend
 * having a bad time.
 */
const NEARBY_RECONCILE_TICK_MS = 60_000;


export default function Home() {
  const { t } = useTranslation();
  const router = useRouter();
  const { isDark } = useAppTheme();

  const colors = useMemo(() => getHomeColors(isDark), [isDark]);

  const styles = useMemo(() => createStyles(colors, isDark), [colors, isDark]);

  const { width: windowWidth } = useWindowDimensions();

  const currentUser = useAuthStore((state) => state.user);



  const venue = useSessionStore((state) => state.venue);
  const sameSpaceVenueId = useSessionStore((state) => state.sameSpaceVenueId);
  const sameSpaceVenueName = useSessionStore(
    (state) => state.sameSpaceVenueName,
  );
  const sameSpaceActive = useSessionStore((state) => state.sameSpaceActive);
  const enterSameSpace = useSessionStore((state) => state.enterSameSpace);
  const leaveSameSpace = useSessionStore((state) => state.leaveSameSpace);
  /**
   * Open Join Space. There is one, and picking a venue in it opts you in.
   *
   * There used to be two: a "plain" location edit that wrote only
   * `presence.venue_id`, and this one. Keeping them apart was defended as two
   * consents on one screen — joining says where you are, Same Space says
   * strangers there may see you — and the defence was wrong on the facts. The
   * plain edit had no lasting effect to consent to: `GET /venues/current`
   * re-resolves `venue_id` from GPS on every heartbeat
   * (`PRESENCE_HEARTBEAT_MS`, 120 s) and `heartbeatVenuePatch` replaces it
   * unconditionally, so "joining" a space reverted within two minutes and
   * always had.
   *
   * What it did produce was two spaces on one screen: the picker badged the
   * RESOLVER'S GUESS as "Current" while the space the user had actually
   * chosen sat in Same Space, named differently. Reported from a device.
   *
   * So there is one act. Picking a venue proves proximity and records the
   * participation, and the subtitle says what that means, because it is a real
   * consent even though the thing it replaced was not.
   */
  function openJoinSpace() {
    setPeopleFiltersOpen(false);
    setEditLocationOpen(true);
  }
  // The mode the LIST is actually reading, from the store's own definition
  // rather than a second copy of the rule here — the sheet has to agree with
  // the read, or it shows one list while offering the controls of the other.
  const nearbyMode = effectiveNearbyMode({ sameSpaceVenueId });

  const nearby = useSessionStore((state) => state.nearby);
  const nearbyUnfilteredTotal = useSessionStore(
    (state) => state.nearbyUnfilteredTotal,
  );

  const sentCueIds = useSessionStore((state) => state.sentCueIds);

  const locationState = useSessionStore((state) => state.locationState);

  const nearbyError = useSessionStore((state) => state.nearbyError);

  const nearbyRecovering = useSessionStore((state) => state.nearbyRecovering);

  const nearbyErrorAcknowledged = useSessionStore(
    (state) => state.nearbyErrorAcknowledged,
  );

  const acknowledgeNearbyError = useSessionStore(
    (state) => state.acknowledgeNearbyError,
  );

  const loadingVenue = useSessionStore((state) => state.loadingVenue);

  const refreshVenue = useSessionStore((state) => state.refreshVenue);

  const refreshNearby = useSessionStore((state) => state.refreshNearby);
  const setNearbyListVisible = useSessionStore(
    (state) => state.setNearbyListVisible,
  );
  const loadMoreNearby = useSessionStore((state) => state.loadMoreNearby);
  const loadingMoreNearby = useSessionStore((state) => state.loadingMoreNearby);
  const saveNearbyRadius = useSessionStore((state) => state.saveNearbyRadius);
  const savePeopleFilters = useSessionStore((state) => state.savePeopleFilters);

  const sendCue = useSessionStore((state) => state.sendCue);

  const setVisibility = useSessionStore((state) => state.setVisibility);

  const visibilityStatus = useSessionStore((state) => state.visibilityStatus);
  const publishBlocked = useSessionStore((state) => state.publishBlocked);

  // Whether the people fetch has come back at least once. Sticky, so later
  // refreshes leave the current list on screen rather than emptying it. An
  // unloaded list and an empty one look identical, and "no one is here" is not
  // something to say before asking.
  const [peopleLoaded, setPeopleLoaded] = useState(false);
  /**
   * Whether people have been read once in this mount.
   *
   * The focus effect used to re-read them EVERY time Home regained focus, and
   * `refreshNearby` is not a cheap re-read: it is the first page by
   * definition, so it discards every page the user scrolled in and rewinds the
   * list to 75 rows. Opening a profile and pressing back therefore reset the
   * list — the "constant refresh" that reads as a hard reload even though the
   * panel never blanks (it keeps its rows while a read is in flight).
   *
   * A ref rather than state: it must not re-register the effect it guards.
   * Left DOWN when the read throws, so a failed first read is retried on the
   * next focus instead of leaving the screen permanently empty.
   */

  // The same, for the space lookup — and the reason `loadingVenue` alone will
  // not do. On the frame the screen mounts, the lookup has not started yet, so
  // `loadingVenue` is still false; keying off it alone flashes a "no space"
  // card for one frame before the spinner replaces it.
  const [spaceLoaded, setSpaceLoaded] = useState(false);

  const [refreshing, setRefreshing] = useState(false);

  const [editLocationOpen, setEditLocationOpen] = useState(false);

  const [activePanel, setActivePanel] = useState<HomePanel>("people");

  const [peopleFiltersOpen, setPeopleFiltersOpen] = useState(false);

  // Read from the profile, not held here. The server applies these to the list
  // AND to the nearby alert, which fires with the app closed — so the saved copy
  // is the only one that means anything, and a local copy could disagree with
  // what is actually being applied.
  const peopleFilters = useMemo(
    () => peopleFiltersFromSaved(currentUser?.peopleFilters),
    [currentUser?.peopleFilters],
  );
  // Returns the save's promise rather than voiding it: the age slider holds
  // the thumb where it was released until this settles, because nothing here
  // is local state — `peopleFilters` is read back off `currentUser`, so the
  // prop cannot move until the write and the nearby refresh have both landed.
  const setPeopleFilters = useCallback(
    (next: PeopleFilters) => savePeopleFilters(savedFromPeopleFilters(next)),
    [savePeopleFilters],
  );

  const [groupFiltersOpen, setGroupFiltersOpen] = useState(false);

  const [groupFilters, setGroupFilters] =
    useState<GroupFilters>(EMPTY_GROUP_FILTERS);

  const [groups, setGroups] = useState<SmallGroup[]>([]);

  const [groupsLoading, setGroupsLoading] = useState(false);

  const [createGroupOpen, setCreateGroupOpen] = useState(false);

  const [creatingGroup, setCreatingGroup] = useState(false);

  const [groupName, setGroupName] = useState("");

  const [groupVibe, setGroupVibe] = useState("Casual Hello");

  const [groupCustomGoal, setGroupCustomGoal] = useState("");

  const [groupAgeRange, setGroupAgeRange] = useState("Any age");

  const [groupSize, setGroupSize] = useState(4);

  const [groupDuration, setGroupDuration] = useState(DEFAULT_GROUP_DURATION);
  // The host's join range. The slider now runs to 10 km for groups too, but
  // the DEFAULT stays the 5 km middle anchor — same number it has always
  // been, and raising the ceiling must not silently quadruple the area of
  // every group whose host never touched the slider.
  const [groupRadiusM, setGroupRadiusM] = useState(NEARBY_RADIUS_DEFAULT_M);

  const [groupPrompt, setGroupPrompt] = useState("");

  const [joinTarget, setJoinTarget] = useState<SmallGroup | null>(null);

  const [joiningGroupId, setJoiningGroupId] = useState<string | null>(null);

  const [visibilityCueTarget, setVisibilityCueTarget] =
    useState<PendingCueTarget | null>(null);

  const [visibilityPromptEnabled, setVisibilityPromptEnabled] = useState(true);

  const [sendingVisibilityCue, setSendingVisibilityCue] = useState(false);

  const [visibilityGroupIntent, setVisibilityGroupIntent] =
    useState<PendingGroupIntent | null>(null);

  const [visibilityGroupPromptEnabled, setVisibilityGroupPromptEnabled] =
    useState(true);

  const [confirmingVisibilityGroup, setConfirmingVisibilityGroup] =
    useState(false);

  // The conjunction, never the switch alone: the server may be refusing to
  // publish a position the user has asked to share, and a header that says Live
  // then has the user believing they are discoverable when they are not.
  const isVisible = isLive({ desired: visibilityStatus, blocked: publishBlocked });

  // Still working out where the user is: from mount until the first lookup
  // settles, and during any later lookup that has nothing to fall back on. A
  // refresh of an already-known space does not count — the panel stays put
  // rather than blinking out under them.
  const locatingSpace =
    (!venue && (loadingVenue || !spaceLoaded)) || nearbyRecovering;

  /**
   * The failure the screen would show if nothing hid it.
   *
   * `nearbyRecovering` blanks it outright. The store already withholds
   * `nearbyError` while it retries, but this expression has a second source —
   * `locationState`, which is still "unavailable" for the whole of a fix we are
   * quietly re-taking — and reading that alone would paint "we can't find your
   * location" over the top of the retry that was about to find it.
   */
  const nearbyFailure = nearbyRecovering
    ? null
    : (nearbyError ??
      (locationState === "denied"
        ? "permission_denied"
        : locationState === "device_location_off"
          ? "device_location_off"
          : locationState === "unavailable"
            ? "location_timeout"
            : null));

  /**
   * The failure the screen actually paints, which an acknowledgement can hide.
   *
   * Only the BLOCK is dismissed. `nearbyFailure` still withholds the people
   * panel below, because "you asked to get on with it" is not the same claim as
   * "we can now see who is nearby", and an empty list under a dismissed error
   * would read as an empty room.
   */
  const blockingNearbyError = nearbyErrorAcknowledged ? null : nearbyFailure;

  /**
   * Whether the screen knows where "here" is.
   *
   * Everything below the space card is a claim about a place: "these people are
   * here now", "no one is here now". With no fix, neither is something we can
   * honestly say, so the panel is withheld entirely rather than left standing
   * empty beside a card that admits we do not know where the user is.
   */
  const hasLocation = !nearbyFailure && !locatingSpace;

  // Approximate location runs the rest of opncue fine, but it is off by enough
  // to make "nearby" meaningless — so who is nearby is the one thing it cannot
  // answer, and the panel needs to know which grant is in hand.
  //
  // Starts at "fine" so the common case never flashes the upgrade prompt while
  // the real answer is still being read.
  const [precision, setPrecision] = useState<LocationPrecision>("fine");

  /**
   * The precision the last focus observed, so a CHANGE can be detected.
   *
   * A ref, not state: this is read inside the focus effect and must not
   * re-register it. Seeded to the optimistic initial value, which only ever
   * makes the first focus look like a change — and the first focus reads the
   * space anyway.
   */
  const precisionRef = useRef<LocationPrecision>("fine");
  /** Whether the space has been read once in this mount. See the focus effect. */
  const venueReadOnceRef = useRef(false);

  const syncPrecision = useCallback(async () => {
    const next = await getLocationPrecision();
    setPrecision(next);
    return next;
  }, []);

  const allowPreciseLocation = useCallback(async () => {
    setPrecision(await requestPreciseLocation());
    // Forced: the grant just changed, which is exactly the thing the refresh
    // gate cannot see. A skipped read here would leave the coarse fix standing.
    await refreshVenue({ force: true });
  }, [refreshVenue]);

  const retryNearby = useCallback(
    async (error: NearbyError) => {
      if (!isLocationError(error)) {
        await refreshVenue({ force: true });
        return;
      }

      // "Try again" is a user press, which is the one context where asking is
      // allowed — `getCurrentCoords` never prompts by design, so a plain
      // refresh here re-reads the same refusal and repaints the same screen,
      // with the OS prompt unable to ever come back. That was the limited-mode
      // loop this handler replaces.
      const access = useLocationAccessStore.getState().access;
      switch (locationRetryAction(access, Platform.OS === "web")) {
        case "request": {
          // Nothing awaited in front of the request: on web the browser only
          // shows its prompt while this press is still the reason it is asked.
          const next = await requestLocationAccess();
          useLocationAccessStore.getState().setAccess(next);
          // Still refused. The screen already says what is wrong, and a read
          // now would only repaint it — the refusal was the user's answer.
          if (next !== "ready") return;
          break;
        }

        case "open-app-settings":
          await openAppSettings();
          return;

        case "open-location-settings":
          await openLocationSettings();
          return;

        case "show-steps":
          notify(
            locationStepsTitle(access, t),
            getLocationAccessSteps(access, getWebEnvironment(), t)?.join("\n"),
          );
          return;

        case "refresh":
          break;
      }

      await refreshVenue({ force: true });
    },
    [refreshVenue, t],
  );

  // People nearby are resolved from GPS radius on the server and never require
  // a detected *venue* — someone on a street, in a park, or between venues must
  // still see and be seen. A venue is optional context; a location fix is not.
  const showPeoplePanel = hasLocation && !editLocationOpen;

  // Small groups are location-scoped: people nearby can find and join them
  // even when the current place is not a named venue.
  const showingGroupsPanel =
    showPeoplePanel && activePanel === "groups";

  /**
   * Are person rows actually on screen? `listData` returns before pushing any
   * when any of these is wrong, so this is the honest subject of anything that
   * exists to keep the people list true.
   *
   * Coarse precision belongs here for the same reason as the groups panel and
   * not by analogy: the list is REPLACED by `peoplePreciseRequired`, because
   * who is nearby is the one claim an approximate fix cannot support.
   * `onEndReached` already refuses to page in that state; this is the same
   * rule applied to the read that nobody asked for.
   */
  const peopleListVisible =
    showPeoplePanel && !showingGroupsPanel && precision !== "coarse";

  const peoplePanelWidth =
    Platform.OS === "web"
      ? Math.max(
          0,
          Math.min(
            windowWidth - CONTENT_SIDE_PADDING * 2,
            PEOPLE_PANEL_MAX_WIDTH,
          ),
        )
      : windowWidth;

  const filterLocks = useMemo(
    () => profileFilterLocks(currentUser),
    [currentUser],
  );

  const profileNearbyRadiusM = clampNearbyRadiusM(currentUser?.nearbyRadiusM);

  const activePeopleFilters = useMemo(
    () => usableFilters(peopleFilters, filterLocks),
    [peopleFilters, filterLocks],
  );

  // The server applies the filters now, inside the same predicate that produces
  // the count — so the list, the headcount and the paging all describe one
  // population. Nothing is narrowed here: filtering a page that has already
  // been capped and counted is the defect `presentRows` warns about, and doing
  // it on this side was the same defect one layer further out.
  const filteredNearby = nearby;

  const activeFilterCount = filterCount(
    activePeopleFilters,
    profileNearbyRadiusM,
    nearbyMode === "same_space",
  );

  // No client-side narrowing left: `groups` is already the filtered answer.
  const filteredGroups = groups;

  const activeGroupFilterCount = groupFilterCount(groupFilters);

/**
 * The group's age range, as the two floors the API takes.
 *
 * Spread rather than returned as fields so "Any age" sends neither key: the
 * columns are nullable together, and a half range is refused server-side rather
 * than half-applied.
 */
function bandFloorsForCreate(
  label: string,
): { ageBandMinFloor?: number; ageBandMaxFloor?: number } {
  if (label === ANY_AGE_RANGE_LABEL) return {};
  const range = parseBandRangeLabel(label);
  if (!range) return {};
  return { ageBandMinFloor: range.minFloor, ageBandMaxFloor: range.maxFloor };
}

  const listData = useMemo<HomeListItem[]>(() => {
    const items: HomeListItem[] = [{ type: "header" }, { type: "tabs" }];

    if (!showPeoplePanel) return items;

    if (showingGroupsPanel) {
      items.push({ type: "groups" });
      return items;
    }

    // Who is nearby is the one claim approximate location cannot support. It
    // resolves to an area wide enough to hold the whole 1.5km range, so a list
    // built on it would put strangers kilometres away in front of each other
    // and call them close by. Everything else on this screen still works.
    if (precision === "coarse") {
      items.push({ type: "peoplePreciseRequired" });
      return items;
    }

    if (filteredNearby.length > 0) {
      items.push(
        ...filteredNearby.map((person) => ({
          type: "person" as const,
          person,
        })),
      );
      return items;
    }

    // Nothing to show yet versus nothing to show — only the second is worth
    // saying. `peopleLoaded` is sticky, so a pull-to-refresh keeps whatever is
    // already on screen rather than replacing it with a spinner. An empty list
    // with a page still IN FLIGHT — the store's rendered-empty auto-fill — is
    // the first kind too: "nobody around" must wait until the fill settles or
    // gives up, or it flashes as a false empty between pages.
    items.push({
      type:
        peopleLoaded && !loadingMoreNearby ? "peopleEmpty" : "peopleLoading",
    });

    return items;
  }, [
    filteredNearby,
    loadingMoreNearby,
    peopleLoaded,
    precision,
    showingGroupsPanel,
    showPeoplePanel,
  ]);

  /**
   * The list has been scrolled to the bottom: fetch the next page of people.
   *
   * Gated on the people panel actually being what is on screen. The same
   * FlatList renders the groups panel and the precise-location prompt, and both
   * are short enough to sit entirely within the viewport — which makes
   * `onEndReached` fire on layout, with no scroll involved at all. Without this
   * guard, opening the groups tab would quietly page through the people list
   * nobody is looking at.
   *
   * Everything else that could fire this twice is handled in the store:
   * `loadMoreNearby` is a no-op with a page in flight or no cursor left.
   */
  const onEndReached = useCallback(() => {
    if (!showPeoplePanel || showingGroupsPanel) return;
    if (precision === "coarse") return;
    void loadMoreNearby();
  }, [loadMoreNearby, precision, showPeoplePanel, showingGroupsPanel]);

  /**
   * Only while a page is genuinely on its way, and never for the first one —
   * that is the whole screen loading and it has its own spinner. A footer that
   * showed whenever a cursor existed would read as "still loading" for a list
   * that is sitting still, waiting to be scrolled.
   */
  const showPagingFooter =
    loadingMoreNearby && showPeoplePanel && !showingGroupsPanel;

  const peopleFilterOptions = useMemo(
    () => ({
      languages: FILTER_OPTION_LISTS.languages.map((option) => option.value),
      friendshipGoals: FILTER_OPTION_LISTS.friendshipGoals.map(
        (option) => option.value,
      ),
    }),
    [],
  );

  async function updateNearbyRadius(nextRadiusM: number) {
    try {
      // The store action persists the radius and re-reads people; the groups
      // list is this screen's own radius-scoped state, so it re-reads here.
      if (await saveNearbyRadius(nextRadiusM)) {
        await refreshGroups();
      }
    } catch (err) {
      notify(
        t(homeStrings.rangeFailed),
        err instanceof Error ? err.message : t(homeStrings.genericError),
      );
    }
  }

  /**
   * Leave the joined space, reporting a failure rather than swallowing it.
   *
   * Shared by the two controls that can end a participation — choosing
   * Nearby, and Clear — because they make the same promise and a promise kept
   * in one place and not the other is worse than either. The failure is SHOWN:
   * the server is what stops listing you, so a silent failure would leave
   * somebody published in a room they believe they have left.
   *
   * Only when there is something to leave. Tapping a control that already
   * matches the state must not spend a DELETE, and must not raise a "could
   * not leave" about a space nobody was in.
   */
  function leaveSameSpaceOrReport() {
    if (sameSpaceVenueId === null) return;
    void leaveSameSpace().catch((error: unknown) =>
      notify(
        t(homeStrings.sameSpaceLeaveFailedTitle),
        error instanceof Error ? error.message : t(homeStrings.genericError),
      ),
    );
  }

  // The radius slider lives in both filter sheets, so Clear covers it too —
  // back to the widest default. Without this, Clear looks dead whenever every
  // other field already sits at its default and only the range was narrowed.
  //
  // And Same Space, for the same reason plus a stronger one: it is counted in
  // the badge now, so a Clear that left it on would leave a filter showing as
  // applied with no control claiming to have cleared it — and the empty-list
  // "Clear filters" button, whose whole job is to explain why the screen is
  // empty, would leave it empty.
  function clearPeopleFilters() {
    setPeopleFilters(EMPTY_PEOPLE_FILTERS);
    void updateNearbyRadius(NEARBY_RADIUS_DEFAULT_M);
    leaveSameSpaceOrReport();
  }

  function clearGroupFilters() {
    setGroupFilters(EMPTY_GROUP_FILTERS);
    void updateNearbyRadius(NEARBY_RADIUS_DEFAULT_M);
  }

  /**
   * Re-read the groups the viewer can join, narrowed by what they asked for.
   *
   * Depends on `groupFilters`, so changing a filter re-reads rather than
   * re-filtering what is already here — the same rule the radius follows. The
   * server caps the list, so filtering on this side would fill that cap with
   * groups the viewer did not ask for and hide ones they did.
   */
  /**
   * The rows we are currently showing, readable without depending on them.
   *
   * `refreshGroups` runs from `useFocusEffect`; adding `groups` to its deps
   * would change its identity on every load and re-trigger the focus effect
   * that called it.
   */
  const groupsRef = useRef<SmallGroup[]>([]);

  /**
   * Which query the rows on screen are the answer to.
   *
   * Keeping rows visible through a refresh is only honest while the QUESTION is
   * unchanged. Changing a filter re-reads (see the note above), and showing the
   * previous filter's rows until the network returns is a list that is
   * confidently answering something nobody asked.
   */
  const groupsQueryKeyRef = useRef<string>("");
  const groupsQueryKey = useMemo(
    () => JSON.stringify(groupBrowseFilters(groupFilters)),
    [groupFilters],
  );

  const refreshGroups = useCallback(async () => {
    // Only claim to be loading when there is nothing honest to show.
    //
    // `SmallGroupsPanel` renders a spinner INSTEAD of the rows whenever this
    // is true, and this runs on every focus — so a correct list was being
    // replaced by a spinner for the length of a network call, every time the
    // user came back to the tab. That is the "I created a group and had to
    // wait for it to appear" report: the group was there, and we hid it.
    //
    // A CHANGED query is the exception, and it is why this is not simply an
    // emptiness check: those rows answer the old filters.
    setGroupsLoading(
      groupsRef.current.length === 0 || groupsQueryKeyRef.current !== groupsQueryKey,
    );

    try {
      const next = await groupApi.list(groupBrowseFilters(groupFilters));
      groupsRef.current = next;
      groupsQueryKeyRef.current = groupsQueryKey;
      setGroups(next);
    } catch {
      // Keep what we have. This used to `setGroups([])`, which turned any
      // transient failure of a BACKGROUND re-read into "your groups vanished"
      // — the list was correct until we emptied it.
      if (groupsRef.current.length === 0) setGroups([]);
    } finally {
      setGroupsLoading(false);
    }
  }, [groupFilters, groupsQueryKey]);

  /**
   * Fold a group we were just handed into the rows already on screen.
   *
   * `groupApi.create`/`join` both return the complete group (`GroupService`
   * ends `return this.get(group.id, userId)`), so the round trip that used to
   * run before navigating was re-fetching something we already had.
   *
   * **Updates only. It never inserts, and that is deliberate.** Browse is not a
   * list of groups that exist, it is the answer to "which groups may this
   * viewer join" — `eligibleNearbyQuery` applies `ST_DWithin` and
   * `withinAgeBandRangeSql` in the WHERE, before any browse filter. Deciding
   * here that a new group belongs would mean a second copy of that rule living
   * on the client, and a group whose age range excludes its own creator is
   * correctly hidden by the server and would be wrongly shown by us. It would
   * also push the list past the server's `GROUP_LIST_LIMIT` ceiling, which
   * nothing on this screen is allowed to do.
   *
   * A row the server already chose to show is different: updating it in place
   * states no new opinion about eligibility, and keeps the `created_at DESC`
   * order the server sorted by rather than jumping a joined group to the top.
   *
   * The created group still arrives quickly — the refresh is fired as we
   * navigate, and the list no longer blanks while it runs.
   */
  const updateKnownGroup = useCallback((group: SmallGroup) => {
    setGroups((prev) => {
      if (!prev.some((item) => item.id === group.id)) return prev;
      const next = prev.map((item) => (item.id === group.id ? group : item));
      groupsRef.current = next;
      return next;
    });
  }, []);

  useFocusEffect(
    useCallback(() => {
      // Groups are location-scoped, so they wait on the space refresh — that is
      // what checks presence in at the current GPS point. People go in parallel;
      // the server resolves them from the coordinates it already has.
      void (async () => {
        // The PRECISION read stays on every focus. It is a local permission
        // check — no network, no GPS — and it is how coming back from the
        // system settings page having granted precise location updates the
        // panel instead of leaving the upgrade prompt standing over a grant
        // that now exists.
        const before = precisionRef.current;
        const after = await syncPrecision();
        precisionRef.current = after;

        // The SPACE read does not. `refreshVenue` acquires GPS and calls
        // `venues/current`, and that endpoint is a CHECK-IN: it writes
        // presence and resolves cell membership. It is the same call the load
        // harness uses as its foreground heartbeat. Running it per focus meant
        // Home -> chat -> Home -> profile -> Home was three GPS acquisitions
        // and three presence writes for somebody standing still, and at
        // millions of users that is a heartbeat under another name — the exact
        // shape `POST /presence/anchor` was rejected for on cost.
        //
        // What actually changes the answer is narrow: the first read, and a
        // precision grant made OUTSIDE the app. An in-app grant already forces
        // its own refresh (`allowPreciseLocation`), movement never arrives
        // through focus (the location task publishes it), and resume and
        // reconnect belong to the resync gate.
        if (venueReadOnceRef.current && before === after) {
          setSpaceLoaded(true);
          return;
        }

        try {
          // Only a read that actually RAN counts. `refreshVenue` resolves
          // without reading when the launch gate coalesces it into another
          // caller's in-flight read — at cold launch the tab layout's
          // `refreshAll` and this effect fire in the same frame, so that is the
          // normal case, not a rare one. Treating the skip as a success let a
          // FAILING owner-read be recorded here as done, and no later focus
          // would retry it. It also fails closed on a read that threw.
          if (await refreshVenue()) venueReadOnceRef.current = true;
        } finally {
          // In `finally`, so a lookup that fails outright still releases the
          // screen instead of leaving the spinner up for good.
          setSpaceLoaded(true);
        }
        // Groups are location-scoped, so they follow the SPACE rather than the
        // focus: re-reading them without a new space re-asks a question whose
        // inputs have not moved. Everything that changes a group for other
        // reasons — creating, joining, leaving, opening the panel — already
        // refreshes them at its own call site.
        await refreshGroups();
      })();

      void (async () => {
        // Not on every focus, and not only on the first: whichever of those
        // this screen picked, it was wrong in one direction.
        //
        // Every focus re-read was removed because coming back from a profile,
        // a chat or the settings app is not new information about who is
        // nearby, and it cost the user their place in the list. First focus
        // only replaced it, on the grounds that what keeps people fresh is
        // already built — presence events converge the list live
        // (`people:joined` / `people:hidden` both call `nearbyEventRefresh` in
        // the tab layout), the resync gate re-reads on resume and on socket
        // reconnect, and pull-to-refresh is the explicit ask.
        //
        // Each of those is real and each has the same hole: they all need
        // somebody to ANNOUNCE the change. A lease running out and a liveness
        // key expiring announce nothing — no publish, no event — and a publish
        // that does happen can still miss its audience, which is what
        // `realtime_audience_degraded_total` and `..._saturated_total` count.
        // Under "first focus only" a row could then outlive the person for as
        // long as the app stayed open, which on a screen whose whole claim is
        // "these people are here now" is the worst kind of wrong.
        //
        // So: a bound, not a boolean. Returning two minutes later re-reads;
        // bouncing back from a chat does not.
        //
        // Two windows, not a window and an escape hatch. `refreshNearby` is
        // the first page BY DEFINITION — it drops the cursor and every page
        // that was scrolled in — so re-reading a list somebody has paged
        // through rewinds 150 rows to 75 and loses their place. That earns
        // them a longer bound, because they are looking straight at the list
        // and events still converge it. It does not earn them an unbounded
        // one: skipping the read outright let a paged list carry a departed
        // row for as long as the app stayed open.
        //
        // Read imperatively, NOT as a subscribed value: this flag changes on
        // every page, and in the dependency list it would re-register the
        // effect it guards.
        const maxAgeMs = useSessionStore.getState().nearbyPaged
          ? NEARBY_PAGED_REFOCUS_MAX_AGE_MS
          : NEARBY_REFOCUS_MAX_AGE_MS;
        try {
          // A skipped read is not recorded as one: the gate only stamps
          // `finishedAt` in `refreshNearby`'s `finally`, which a coalesced
          // call never reaches — so the next focus asks again rather than
          // inheriting somebody else's turn.
          await refreshNearby({ maxAgeMs });
        } catch {
          // Nothing to add: the store has already recorded why in `nearbyError`
          // and the panel renders it. Caught only so the rejection cannot
          // escape a fire-and-forget effect and surface as an uncaught error.
        } finally {
          // In `finally`, so a failed fetch still releases the panel to its
          // empty state instead of leaving a spinner up for good.
          setPeopleLoaded(true);
        }
      })();
    }, [refreshVenue, refreshNearby, refreshGroups, syncPrecision]),
  );

  /**
   * The floor under everything else: a list that goes stale while nobody moves.
   *
   * Every other path that converges Nearby needs somebody to ANNOUNCE the
   * change, and departures do not announce. A lease running out and a liveness
   * key expiring emit nothing — no publish, no event — so the row of somebody
   * who left is corrected by exactly one thing today: a `people:joined` for
   * some unrelated person happening to trigger a full re-read. That made the
   * correctness of the list a side effect of how busy the room was, which is
   * backwards: the quieter it gets, the longer a departed person sits there
   * looking tappable, and tapping them spends a Cue on somebody who is gone.
   *
   * Focus cannot fill the gap, because focus does not re-fire while somebody
   * STAYS on Home — which is exactly where they are when they are choosing who
   * to Cue. So the two windows this screen already defines get a clock instead
   * of only an event.
   *
   * Deliberately NOT a new policy. It reads through the same `maxAgeMs` the
   * focus read uses, so a list is re-read on the same terms whether the user
   * came back to Home or never left. The paged window stays ten minutes for
   * the reason it was granted: a refresh is a first page by definition, so
   * reconciling a list somebody has scrolled through rewinds it, and that cost
   * is only worth paying once the rows are two liveness TTLs old.
   *
   * `background: true`, so a failed reconciliation cannot take the screen. The
   * user asked for nothing here; there are rows in front of them; a full-screen
   * "finding your space" spinner in place of a populated list would be a worse
   * lie than the staleness it was trying to fix.
   *
   * Runs only while the people list is actually RENDERED, which is narrower
   * than "Home is focused" in three ways that all mean the same thing: the
   * groups panel is up, location is missing, or the location editor is open.
   * In each, `listData` returns before it pushes a single person row, so a
   * read would be keeping nothing true (review, 2026-08-28).
   *
   * Reconciling on ENTRY as well as on the tick is what keeps that narrowing
   * from costing correctness. Gating the timer alone would mean ten minutes on
   * the groups tab followed by a stale people list for up to another minute —
   * the window would be honoured by the clock and broken by the user. The
   * entry read goes through the same `maxAgeMs`, so switching panels back and
   * forth inside the window costs a `Map` lookup, not a request.
   *
   * Stops when Home is not focused (the cleanup) and skips while the app is
   * not active (the guard) — a backgrounded phone and a hidden browser tab are
   * the same state here, and neither has anybody looking at a list to keep
   * true.
   */
  useFocusEffect(
    useCallback(() => {
      if (!peopleListVisible) {
        setNearbyListVisible(false);
        return undefined;
      }

      // Tell the socket layer that a list exists to keep true. `_layout` sits
      // ABOVE this screen and has no other way to know, and the coarse
      // `nearby:changed` nudge is worth a read only when this is up.
      setNearbyListVisible(true);

      const reconcile = () => {
        if (AppState.currentState !== "active") return;
        // Imperative, like the focus read's own: this flag changes on every
        // page, and in a dependency list it would re-register the interval it
        // guards — restarting the clock on every scroll, which is the one way
        // a timer like this quietly never fires.
        const { nearbyPaged, nearbyDirty } = useSessionStore.getState();
        const maxAgeMs = nearbyPaged
          ? NEARBY_PAGED_REFOCUS_MAX_AGE_MS
          : NEARBY_REFOCUS_MAX_AGE_MS;
        // A coarse nudge that landed while this list was off screen is the
        // one case the age gate must not win. It is EVIDENCE the list is
        // wrong, and the gate would otherwise sit on it for the rest of the
        // window in front of somebody now looking at it.
        //
        // NEVER ON A PAGED LIST, and that exception is not caution. A refresh
        // is the first page by definition — it drops the cursor and every page
        // scrolled in — so forcing one here would rewind somebody's scroll
        // position to fix a staleness they had not noticed. That is the
        // trade the longer paged window already refused, and a nudge naming
        // nobody is not new evidence that it was wrong. The bit stays set and
        // the paged window drains it on its own terms.
        const forced = nearbyDirty && !nearbyPaged;
        void refreshNearby(
          forced
            ? { force: true, background: true }
            : { maxAgeMs, background: true },
        ).catch(() => {
          // An empty list still rethrows — `background` only swallows a
          // failure that has rows to protect. Nothing to report either way:
          // the store owns `nearbyError` and the panel renders it.
        });
      };

      reconcile();
      const timer = setInterval(reconcile, NEARBY_RECONCILE_TICK_MS);
      return () => {
        clearInterval(timer);
        // Losing focus is losing the list. Left true, a tab switch to Chat
        // would keep every coarse nudge in the cell costing this client a
        // read of a screen nobody is on.
        setNearbyListVisible(false);
      };
    }, [peopleListVisible, refreshNearby, setNearbyListVisible]),
  );

  useFocusEffect(
    useCallback(() => {
      // Close the space picker when Home loses focus. It is a transient
      // correction flow, not a place: tapping Home is a request for Home, and
      // leaving the picker mounted means the tab button appears to do nothing.
      // Its own `visible` effect re-runs the GPS lookup on the way back in, so
      // reopening always starts from a fresh fix rather than a stale one.
      return () => setEditLocationOpen(false);
    }, []),
  );

  async function onRefresh() {
    setRefreshing(true);

    try {
      // Forced: someone pulled the list down. An explicit request for fresh
      // data always does the work, however recently the last read ran.
      await Promise.all([
        refreshVenue({ force: true }),
        refreshNearby({ force: true }),
        refreshGroups(),
      ]);
    } catch {
      // Same as the focus effect: pull-to-refresh reports failure through the
      // panels' own error states, not by throwing out of the handler.
    } finally {
      setRefreshing(false);
    }
  }

  function onChangePanel(panel: HomePanel) {
    setActivePanel(panel);
    if (panel === "groups") void refreshGroups();
  }

  async function onToggleVisibility() {
    // Negate what the switch DISPLAYS (`isVisible` = intent AND not refused),
    // never the bare intent. When a heartbeat publish is refused, the switch
    // shows Hidden while `visibilityStatus` is still "visible" — and a toggle
    // computed from the intent turns the user OFF from a switch that already
    // looked off. Measured on Android, where a jumpy fix trips the refusals
    // often: tap 1 hid them invisibly, tap 2 republished, three taps per
    // toggle. Deriving from `isVisible` makes the tap retry the publish
    // instead, which is what a switch showing Hidden is asking for.
    const next = isVisible ? "invisible" : "visible";
    try {
      await setVisibility(next === "visible");
    } catch (err) {
      // The server refuses to publish somebody at a position it cannot stand
      // behind, and says which part was wrong — no location, too vague for the
      // chosen range, or too old. That sentence is the only actionable thing
      // here, so it is shown rather than swallowed. This used to be a bare
      // `void`, which turned every refusal into an unhandled rejection.
      notify(
        t(homeStrings.visibilityFailed),
        err instanceof Error ? err.message : t(homeStrings.genericError),
      );
    }
  }

  async function sendCueWithFeedback(userId: string) {
    try {
      await sendCue(userId);
    } catch (err) {
      // A cooldown is not a failure the sender can retry their way out of, and
      // the person is still sitting in the list looking tappable — so it gets
      // the localized sentence and a live countdown rather than the server's
      // English, which was rendered before this alert was opened.
      const cooldown = cueCooldownFrom(err);
      notify(
        t(homeStrings.cueFailed),
        cooldown
          ? cueCooldownMessage(cooldown)
          : err instanceof Error
            ? err.message
            : t(homeStrings.genericError),
      );
    }
  }

  async function onSendCue(userId: string, name: string) {
    if (!isVisible) {
      setVisibilityPromptEnabled(true);
      setVisibilityCueTarget({ id: userId, name });
      return;
    }

    await sendCueWithFeedback(userId);
  }

  function closeVisibilityCuePrompt() {
    if (sendingVisibilityCue) return;
    setVisibilityCueTarget(null);
  }

  async function confirmVisibilityCuePrompt() {
    if (!visibilityCueTarget || !visibilityPromptEnabled) return;

    setSendingVisibilityCue(true);

    try {
      await setVisibility(true);
      await sendCueWithFeedback(visibilityCueTarget.id);
      setVisibilityCueTarget(null);
    } catch (err) {
      // Becoming visible is the first half of this flow, so its refusal has to
      // be reported here — the Cue never happens, and `finally` alone would
      // close the spinner over an unhandled rejection.
      //
      // Close FIRST. This prompt is a native Modal and `notify` is another
      // one, so the dialog store holds the notice until this closes. Left
      // open, the refusal is invisible until the person cancels a prompt that
      // looks like it is still working — no freeze, but a dead end. The flow
      // is over either way: there is nothing left to confirm.
      setVisibilityCueTarget(null);
      notify(
        t(homeStrings.visibilityFailed),
        err instanceof Error ? err.message : t(homeStrings.genericError),
      );
    } finally {
      setSendingVisibilityCue(false);
    }
  }

  async function onShareOpnCue() {
    const message = [t(homeStrings.shareMessage), OPNCUE_SHARE_URL]
      .filter(Boolean)
      .join("\n\n");

    try {
      await shareText({
        title: t(homeStrings.shareTitle),
        message,
      });
    } catch {
      notify(t(homeStrings.shareFailed), t(homeStrings.shareFailedBody));
    }
  }

  function requestCreateGroup() {
    if (!isVisible) {
      setVisibilityGroupPromptEnabled(true);
      setVisibilityGroupIntent("create");
      return;
    }

    warmCreateFixCache();
    setCreateGroupOpen(true);
  }

  /**
   * Warm the OS's fix cache while the form is being filled, so the Create tap
   * can be answered from it — `group-create-latency-plan.md` §3.
   *
   * Fire-and-forget and the RESULT IS DISCARDED, deliberately: `ageMs` is
   * computed when a fix is read, so a coordinate held in component state
   * through the form-filling gap would arrive at the server declared fresher
   * than it is — the stale-coordinate bug by choice (`location.ts`). The tap
   * re-reads instead; a cached fix it accepts is dated off its own timestamp.
   *
   * Same bound as the submit read, which is what makes this self-deduplicating:
   * after the become-visible flow's live check-in the cache is seconds old and
   * this returns without touching the location stack.
   */
  function warmCreateFixCache() {
    void getCurrentCoords({
      acceptCachedWithinMs: groupCreateCacheBoundMs(groupRadiusM),
    });
  }

  function closeVisibilityGroupPrompt() {
    if (confirmingVisibilityGroup) return;
    setVisibilityGroupIntent(null);
  }

  async function confirmVisibilityGroupPrompt() {
    if (!visibilityGroupIntent || !visibilityGroupPromptEnabled) return;

    setConfirmingVisibilityGroup(true);

    try {
      await setVisibility(true);
      // NOT awaited. The create form does not read the groups list, and the
      // join sheet takes the group it was opened with — so blocking the form
      // on this read added a whole round trip to a spinner that build 12
      // measured at 8.95s. It repaints the list behind whatever opens next.
      void refreshGroups();
      if (visibilityGroupIntent === "create") {
        // Right after the visibility flow's own live read and check-in,
        // which is what makes the warm free here.
        warmCreateFixCache();
        setCreateGroupOpen(true);
      } else {
        setJoinTarget(visibilityGroupIntent.group);
      }
      setVisibilityGroupIntent(null);
    } catch (err) {
      // Same as the Cue prompt: becoming visible is the first half, and the
      // group flow behind it cannot start. Reported rather than left to reject
      // past the `finally` that only closes the spinner.
      //
      // And closed first for the same reason: this prompt is a Modal, the
      // notice is a Modal, so a notice raised over it waits for it to go.
      setVisibilityGroupIntent(null);
      notify(
        t(homeStrings.visibilityFailed),
        err instanceof Error ? err.message : t(homeStrings.genericError),
      );
    } finally {
      setConfirmingVisibilityGroup(false);
    }
  }

  async function onCreateGroup() {
    if (!isVisible) {
      setVisibilityGroupPromptEnabled(true);
      setVisibilityGroupIntent("create");
      return;
    }

    const name = groupName.trim();
    if (!name) {
      notify(
        t(homeStrings.groupNameRequired),
        t(homeStrings.groupNameRequiredBody),
      );
      return;
    }

    const customGoal = groupCustomGoal.trim();
    const selectedVibe =
      groupVibe === OTHER_GROUP_GOAL ? customGoal : groupVibe;

    if (groupVibe === OTHER_GROUP_GOAL && !customGoal) {
      notify(
        t(homeStrings.customGoalRequired),
        t(homeStrings.customGoalRequiredBody),
      );
      return;
    }

    setCreatingGroup(true);

    // Closed in `finally` as well as on success — a create that throws or
    // times out is a duration worth measuring, not a lost span. Idempotent,
    // so the double close is harmless.
    try {
      // The two awaited segments in front of navigation, timed separately —
      // `group-create-latency-plan.md` §5. Bounded cache allowance per the
      // radius tier: a fix served from cache is dated off its own timestamp,
      // so the server still sees its true age.
      const coords = await getCurrentCoords({
        acceptCachedWithinMs: groupCreateCacheBoundMs(groupRadiusM),
      });
      if (coords.status !== "ok") {
        // Close the form before the notice. `notify` renders through
        // ConfirmDialog, which is a native Modal, and iOS will not present one
        // over this form's Modal — the notice never appears and its window
        // eats every touch. Nothing here is fixable inside the form (the
        // problem is location), so there is no reason to keep it up.
        setCreateGroupOpen(false);
        notify(
          t(homeStrings.groupCreateFailed),
          t(homeStrings.groupLocationRequired),
        );
        return;
      }

      const input: CreateSmallGroupInput = {
        name,
        vibe: selectedVibe,
        // Band floors, not the label. The server validates the pair against the
        // real band set and stores null for anything else, so sending a string
        // it had to parse would turn a mistyped range into "any age" silently.
        // The slider snaps to band edges, so this can only fail if someone
        // reaches this screen with a stored value from before that.
        ...bandFloorsForCreate(groupAgeRange),
        maxMembers: groupSize,
        durationMinutes: groupDuration,
        radiusM: groupRadiusM,
        prompt: groupPrompt.trim() || undefined,
        gpsLat: coords.coords.lat,
        gpsLng: coords.coords.lng,
        gpsAccuracy: coords.coords.accuracyMeters ?? undefined,
        gpsAgeMs: coords.coords.ageMs,
      };
      const group = await groupApi.create(input);
      setCreateGroupOpen(false);
      setGroupName("");
      setGroupCustomGoal("");
      setGroupPrompt("");
      // Navigate first. This used to await the re-read, so the modal closed and
      // the user sat on Home for the length of a network call before the chat
      // opened — measured at ~100ms of fully-visible Home on wifi (SM-G998B,
      // build 9, frame capture), and it is a network call, so it grows with a
      // worse connection. Nothing about opening the group depends on the list.
      updateKnownGroup(group);
      // So the group screen can name itself on its first render instead of
      // flashing "Small Group" until `groupApi.get` answers.
      rememberGroupHeader(group.id, group);
      router.push(`/group/${group.id}`);
      void refreshGroups();
    } catch (err) {
      // Same rule as the location branch above: the form goes before the
      // dialog. A create that threw is not retried by editing this form.
      setCreateGroupOpen(false);
      notify(
        t(homeStrings.groupCreateFailed),
        err instanceof Error ? err.message : t(homeStrings.genericError),
      );
    } finally {
      setCreatingGroup(false);
    }
  }

  async function onJoinGroup(group: SmallGroup) {
    if (group.isMember) {
      rememberGroupHeader(group.id, group);
      router.push(`/group/${group.id}`);
      return;
    }

    if (!isVisible) {
      setVisibilityGroupPromptEnabled(true);
      setVisibilityGroupIntent({ action: "join", group });
      return;
    }

    setJoinTarget(group);
  }

  async function confirmJoinGroup(allowMemberCues: boolean) {
    if (!joinTarget) return;

    setJoiningGroupId(joinTarget.id);

    try {
      const group = await groupApi.join(joinTarget.id, { allowMemberCues });
      setJoinTarget(null);
      // Same shape as creating, and the same fix: joining does not depend on
      // the browse list either.
      updateKnownGroup(group);
      // So the group screen can name itself on its first render instead of
      // flashing "Small Group" until `groupApi.get` answers.
      rememberGroupHeader(group.id, group);
      router.push(`/group/${group.id}`);
      void refreshGroups();
    } catch (err) {
      // `setJoinTarget(null)` is what closes JoinGroupModal, and on the success
      // path above it already runs before navigation. The failure path left it
      // open and raised the notice over it — invisible on iOS, and the window
      // ate every touch. A failed join is not retried from this sheet.
      setJoinTarget(null);
      notify(
        t(homeStrings.groupJoinFailed),
        err instanceof Error ? err.message : t(homeStrings.genericError),
      );
    } finally {
      setJoiningGroupId(null);
    }
  }

  const header = (
    <View style={[styles.header, showPeoplePanel && styles.peoplePanelHeader]}>
      {blockingNearbyError ? (
        // Only ever a resolved failure: the header is not rendered at all while
        // a fix/request is still resolving — the spinner replaces the list then.
        //
        // A blocking nearby error takes precedence over any venue still in
        // state, so the screen says what actually failed instead of naming a
        // potentially stale place.
        <NearbyErrorState
          error={blockingNearbyError}
          onRetry={() => void retryNearby(blockingNearbyError)}
          onAcknowledge={acknowledgeNearbyError}
          onAllowPrecise={
            precision === "coarse"
              ? () => void allowPreciseLocation()
              : undefined
          }
          colors={colors}
          styles={styles}
        />
      ) : venue ? (
        <>
          {editLocationOpen ? (
            <EditLocationEditor
              visible
              // The space the user CHOSE, never `venue.name`. Passing the
              // resolver's guess here is what put two spaces on one screen:
              // the picker badged Powercity "Current" because the heartbeat
              // had re-resolved to it, while the user's own space was Kia. A
              // guess has no business wearing that word.
              //
              // Undefined when there is no participation, so nothing is badged
              // Current — which is the honest answer. We do not know.
              //
              // Ungated by `sameSpaceActive`, like the filter sheet: having
              // walked out of the space you joined does not make it stop being
              // the one you are in. Re-picking it is allowed and the server
              // re-proves proximity, so a stale claim fails there with a
              // reason rather than being hidden here without one.
              currentVenueName={sameSpaceVenueName ?? undefined}
              onClose={() => setEditLocationOpen(false)}
              onUpdated={() => {
                setEditLocationOpen(false);
                // The join proved proximity and wrote the venue; this records
                // the participation, which is the part that lasts. A refusal
                // is the server declining the CLAIM — a fix gone stale or
                // vague between the two calls — so it is shown rather than
                // swallowed into an empty list with no explanation.
                void enterSameSpace().catch((error: unknown) =>
                  notify(
                    t(homeStrings.sameSpaceFailedTitle),
                    error instanceof Error
                      ? error.message
                      : t(homeStrings.genericError),
                  ),
                );
              }}
            />
          ) : (
            <VenueCard
              name={venue.name}
              named={venue.named !== false}
              // The joined space wins over the resolver's guess while the
              // person is IN it: the heartbeat may move `venue` to the
              // business next door, and overwriting the label the user chose
              // with it would be the inferred model reasserting itself on the
              // one screen that had escaped it.
              //
              // `sameSpaceActive`, not merely the name. Walking out does not
              // clear the opt-in — that is the user's own act — but it does end
              // the claim the plain label makes, so the hedge comes back while
              // the participation goes on being disclosed in the filter sheet.
              joinedSpaceName={sameSpaceActive ? sameSpaceVenueName : null}
              onEdit={openJoinSpace}
              colors={colors}
              styles={styles}
            />
          )}
        </>
      ) : (
        // Located but unnamed. Barely reachable: with coordinates the server
        // always resolves something — VenueService.resolveFromCoords falls back
        // to creating a grid venue from reverse geocoding — so this is a floor
        // for the instant before the first refreshVenue lands, not a state the
        // backend produces.
        <ShareInviteCard
          onShare={onShareOpnCue}
          colors={colors}
          styles={styles}
        />
      )}
    </View>
  );

  /**
   * How many people are open to being cued right now: the rendered count,
   * open-ended with a "+" while more pages exist.
   *
   * The server population count is gone (step 10 — it was 83–92% of every
   * Nearby request for one number, and it could not survive Redis liveness).
   * What replaced it keyed the truth on the CURSOR, and that was wrong twice
   * over: a cursor says where a reader resumes, not that anybody is there. A
   * budget-exhausted scan issues one having found nobody, so the plus appeared
   * whenever the read had not finished — and a page that filled at exactly 75
   * never looked at a 76th, so even a full page proved nothing.
   *
   * So the plus is the server's PROOF now (`hasMore`): it takes one more
   * presented candidate than it returns, and says so only if that row existed.
   * `nearby.length` is still what the user can reach on this screen right now,
   * and the count grows as pages load — the number and the list telling the
   * same story.
   *
   * It still counts the right people (the rendered list is already trimmed
   * for pending-Cue peers), still survives having no venue, and at zero it
   * is still the icon alone — no "0", no "just you". A count of nobody is
   * not news worth a sentence, and saying it out loud makes an empty street
   * feel like a verdict.
   */
  const nearbyShownCount = nearby.length;
  // The server's PROOF, not the cursor. A cursor only says where paging
  // resumes, and a budget-exhausted scan issues one having proved nothing — so
  // keying the plus on it made "75+" appear whenever the read had not finished,
  // and even a full page of exactly 75 never proved a 76th person existed.
  const nearbyHasMore = useSessionStore((state) => state.nearbyHasMore);
  const hasPeopleCount = nearbyShownCount > 0;
  const peopleCountChip = (
    <View
      style={styles.topBarPeopleCount}
      // Nothing to announce when the count is zero: the icon carries no number,
      // so it is decorative rather than information being withheld. The empty
      // state below the fold is what tells the story on this screen.
      accessibilityElementsHidden={!hasPeopleCount}
      importantForAccessibility={hasPeopleCount ? "yes" : "no-hide-descendants"}
    >
      <Ionicons name="people-outline" size={16} color={colors.primary} />

      {hasPeopleCount ? (
        <Text numberOfLines={1} style={styles.topBarPeopleCountText}>
          {/*
            The "+" belongs to the NUMBER, not to the phrase. Appending it
            after the translated string produced "150 people+", and in a
            language whose word order puts the noun first it would have landed
            somewhere stranger still. `count` stays for plural selection;
            `display` is what the string interpolates.
          */}
          {t(homeStrings.peopleCount, {
            count: nearbyShownCount,
            display: `${nearbyShownCount}${nearbyHasMore ? "+" : ""}`,
          })}
        </Text>
      ) : null}
    </View>
  );

  return (
    <SafeAreaView style={styles.safe} edges={["top"]}>
      <StatusBar style={isDark ? "light" : "dark"} />

      <View style={styles.topBar}>
        <View style={styles.topBarInner}>
          {/*
            Gated on the fix and NOTHING else.

            It used to require a named venue as well, which quietly made
            visibility a feature of being in a recognised place. It is not: a
            visible user gives us their GPS and we resolve somewhere nearby from
            it. Someone on a street, in a park, or between venues could not turn
            themselves on at all — and being unable to become visible is being
            unable to be cued, so the people most in need of the toggle were the
            ones denied it.

            Location is the real gate, because without a fix there is no radius
            to be visible within.
          */}
          {hasLocation ? (
            <View style={styles.topBarControls}>
              <Pressable
                accessibilityRole="switch"
                accessibilityLabel={t(homeStrings.visibilityA11y)}
                accessibilityState={{ checked: isVisible }}
                onPress={onToggleVisibility}
                style={({ pressed }) => [
                  styles.topBarVisibilityButton,
                  isVisible && styles.topBarVisibilityButtonActive,
                  pressed && styles.topBarVisibilityButtonPressed,
                ]}
              >
                <Ionicons
                  name={isVisible ? "eye-outline" : "eye-off-outline"}
                  size={17}
                  color={isVisible ? colors.green : colors.danger}
                />

                <View style={styles.topBarVisibilityCopy}>
                  <Text
                    numberOfLines={1}
                    style={[
                      styles.topBarVisibilityLabel,
                      isVisible && styles.topBarVisibilityLabelActive,
                    ]}
                  >
                    {t(isVisible ? homeStrings.visible : homeStrings.hidden)}
                  </Text>

                  <Text numberOfLines={1} style={styles.topBarVisibilityHelper}>
                    {t(
                      isVisible
                        ? homeStrings.visibleHint
                        : homeStrings.hiddenHint,
                    )}
                  </Text>
                </View>

                <CompactSwitch enabled={isVisible} disabled={false} />
              </Pressable>

              {peopleCountChip}
            </View>
          ) : (
            <Text style={styles.topBarTitle}>
              {t(homeStrings.findYourSpace)}
            </Text>
          )}

          {/*
            The Live badge marks not being in a named place, which is a
            different question from whether we have a fix. The count no longer
            appears here: it travels with the toggle now that the toggle is
            shown to everyone with a fix.

            Gated on `isVisible` as well as on the venue. It was on the venue
            alone, which made it a SECOND Live surface free to disagree with the
            toggle beside it — and it is the pill that actually appeared in the
            reported bug, because the old catch blanked the venue. Whatever it
            means about placement, a green "Live" shown to someone who is hidden
            or whose position the server has refused is false on its face.
          */}
          {venue || !isVisible ? null : (
            <View style={styles.topBarUnplacedMeta}>
              <View style={styles.liveBadge}>
                <View style={styles.liveDot} />

                <Text style={styles.liveText}>{t(homeStrings.live)}</Text>
              </View>
            </View>
          )}
        </View>
      </View>

      <View
        style={
          showPeoplePanel
            ? [styles.peoplePanelFrame, { width: peoplePanelWidth }]
            : styles.listFrame
        }
      >
        {/*
          One wait, one spinner. While the fix is resolving there is nothing
          below the space card to show anyway, so the spinner stands for the
          whole screen — the earlier arrangement put this behind a gate *and*
          then showed a second spinner inside the card for the same GPS read.
          Everything after the fix renders in the list.
        */}
        {locatingSpace ? (
          <Loading
            label={t(homeStrings.loadingSpace)}
            color={colors.primary}
          />
        ) : (
          <FlatList
            style={styles.listScroller}
            data={listData}
            keyExtractor={(item) => {
              if (item.type === "person") return item.person.id;
              return item.type;
            }}
            stickyHeaderIndices={[1]}
            contentContainerStyle={[
              styles.list,
              showPeoplePanel && styles.peoplePanelList,
              // With a blocking error the list holds one thing, so it centres
              // in the space rather than hanging off the top bar with the rest
              // of the screen left empty below it.
              blockingNearbyError && styles.listCentered,
            ]}
            showsVerticalScrollIndicator={false}
            onEndReached={onEndReached}
            // Half a viewport from the bottom. Close enough that the next page
            // is usually there before the user reaches the end, far enough that
            // it is not armed the moment the list renders.
            onEndReachedThreshold={0.5}
            ListFooterComponent={
              showPagingFooter ? (
                <Loading
                  fill={false}
                  size="small"
                  label={null}
                  color={colors.primary}
                  style={styles.peoplePagingFooter}
                />
              ) : null
            }
            refreshControl={
              <RefreshControl
                refreshing={refreshing}
                onRefresh={onRefresh}
                tintColor={colors.primary}
                colors={[colors.primary]}
                // Background, not surface — see the inbox's RefreshControl:
                // an invisible plate means the arc is the first thing seen.
                progressBackgroundColor={colors.screen}
              />
            }
            renderItem={({ item, index }) => {
              if (item.type === "header") {
                return header;
              }

              if (item.type === "tabs") {
                if (!showPeoplePanel) return null;

                return (
                  <View style={styles.panelTabsStickyHeader}>
                    <PanelTabs
                      activePanel={activePanel}
                      onChange={onChangePanel}
                      peopleFilterCount={activeFilterCount}
                      groupFilterCount={activeGroupFilterCount}
                      onOpenPeopleFilters={() => setPeopleFiltersOpen(true)}
                      onOpenGroupFilters={() => setGroupFiltersOpen(true)}
                      colors={colors}
                      styles={styles}
                    />
                  </View>
                );
              }

              if (item.type === "groups") {
                return (
                  <SmallGroupsPanel
                    groups={filteredGroups}
                    loading={groupsLoading}
                    filterActive={activeGroupFilterCount > 0}
                    onClearFilters={clearGroupFilters}
                    onCreate={requestCreateGroup}
                    onJoin={onJoinGroup}
                    colors={colors}
                    styles={styles}
                  />
                );
              }

              if (item.type === "peopleLoading") {
                // Sits under a real space card, never beside another spinner:
                // by the time the panel is on screen the location is known, so
                // this is only ever the short wait for the people list itself.
                return (
                  <Loading
                    fill={false}
                    size="small"
                    label={null}
                    color={colors.primary}
                    style={styles.peopleLoading}
                  />
                );
              }

              if (item.type === "peoplePreciseRequired") {
                return (
                  <PreciseRequiredPeople
                    compact={showPeoplePanel}
                    onAllowPrecise={() => void allowPreciseLocation()}
                    colors={colors}
                    styles={styles}
                  />
                );
              }

              if (item.type === "peopleEmpty") {
                // `nearbyUnfilteredTotal`, not `nearby.length`. The list is
                // the filtered answer now, so an empty one is empty either way
                // — reading it would tell someone standing in a crowded room
                // that nobody is around, and hide the one action that fixes
                // their screen. The server only computes this (an existence
                // probe, 1/0) when the filtered page is empty and a filter is
                // set, which is exactly when the two sentences differ.
                return activeFilterCount > 0 &&
                  (nearbyUnfilteredTotal ?? 0) > 0 ? (
                  <EmptyFilteredPeople
                    onClear={clearPeopleFilters}
                    colors={colors}
                    styles={styles}
                  />
                ) : (
                  <EmptyVenue
                    compact={showPeoplePanel}
                    sameSpace={nearbyMode === "same_space"}
                    onShare={onShareOpnCue}
                    colors={colors}
                    styles={styles}
                  />
                );
              }

              return (
                <View>
                  {index > 2 ? <View style={styles.peoplePanelRowDivider} /> : null}

                  <View
                    style={[
                      styles.nearbyRowWrap,
                      showPeoplePanel && styles.nearbyRowInPanel,
                    ]}
                  >
                    <NearbyRow
                      user={item.person}
                      sent={sentCueIds.includes(item.person.id)}
                      onSendCue={() =>
                        onSendCue(item.person.id, item.person.displayName)
                      }
                      compact={showPeoplePanel}
                      onOpenProfile={() =>
                        router.push(
                          `/guest-profile?v=${handleFor(item.person.id)}`,
                        )
                      }
                    />
                  </View>
                </View>
              );
            }}
          />
        )}
      </View>

      <PeopleFilterModal
        visible={peopleFiltersOpen}
        // The SAVED filters, not the suspended ones. Each section renders its
        // lock message INSTEAD of its control, so a suspended value is never
        // shown — but handing the modal the nulled set meant the next edit to
        // any other section wrote those nulls back and destroyed a preference
        // the user had not touched. The server suspends; nothing deletes.
        filters={peopleFilters}
        nearbyRadiusM={profileNearbyRadiusM}
        locks={filterLocks}
        options={peopleFilterOptions}
        onChange={setPeopleFilters}
        onNearbyRadiusChange={updateNearbyRadius}
        nearbyMode={nearbyMode}
        // The label names the space that was JOINED, from the store, never
        // `venue` — that is the inferred current venue and the heartbeat
        // rewrites it, so reading it here made the screen name whichever
        // business the resolver last picked.
        // NOT gated on `sameSpaceActive`: the sheet is where the opt-in is
        // disclosed and cleared, so it must go on naming the space after the
        // person has walked out of it. Only the header label follows `active`.
        joinedSpaceName={sameSpaceVenueName}
        // Choosing Same Space with NO participation goes through Join Space —
        // that is the opt-in, and there is no "confirm what we guessed" path
        // to offer, because the guess resolves to the nearest business rather
        // than the room somebody is in. With a participation already recorded
        // it only shows that list; changing which space it is has its own
        // action below.
        onSelectSameSpace={() => {
          // Already participating means Same Space is ALREADY the selected
          // option — the mode is derived from the participation, so there is
          // no list to switch to and no consent left to ask for. Tapping the
          // option you are on does nothing, which is what a radio group is
          // supposed to do; changing which space it is has its own action.
          if (sameSpaceVenueId) return;
          openJoinSpace();
        }}
        // Always Join Space, even while participating — this is how somebody
        // moves from one space to another without having to leave first.
        onChangeSpace={openJoinSpace}
        // Choosing Nearby IS the opt-out — see `leaveSameSpaceOrReport`, which
        // Clear shares so the two cannot drift.
        onSelectNearby={leaveSameSpaceOrReport}
        onClear={clearPeopleFilters}
        onClose={() => {
          setPeopleFiltersOpen(false);
          // Done means "show me the result". The sheet covers the list, so
          // whatever landed while it was open landed unseen, and closing it is
          // the moment the answer has to be right.
          //
          // Deliberately UNFORCED. Every control in the sheet already re-reads
          // when it changes something, so forcing here would spend a second
          // round trip on the common path to fetch what was just fetched. The
          // launch-refresh gate answers exactly this question — a read in
          // flight or completed in the last few seconds is skipped, anything
          // staler is repeated — so Done costs nothing when nothing changed
          // and re-reads when something did.
          void refreshNearby().catch(() => {
            // The store owns `nearbyError` and the panel renders it; a second
            // report here would be the same failure said twice.
          });
        }}
        colors={colors}
        styles={styles}
      />

      <GroupFilterModal
        visible={groupFiltersOpen}
        filters={groupFilters}
        nearbyRadiusM={profileNearbyRadiusM}
        onChange={setGroupFilters}
        onNearbyRadiusChange={updateNearbyRadius}
        onClear={clearGroupFilters}
        onClose={() => setGroupFiltersOpen(false)}
        colors={colors}
        styles={styles}
      />

      <VisibilityCuePrompt
        visible={Boolean(visibilityCueTarget)}
        enabled={visibilityPromptEnabled}
        sending={sendingVisibilityCue}
        personName={
          visibilityCueTarget?.name ?? t("social.notifications.someone")
        }
        onToggle={() => setVisibilityPromptEnabled((value) => !value)}
        onCancel={closeVisibilityCuePrompt}
        onConfirm={confirmVisibilityCuePrompt}
        colors={colors}
        styles={styles}
      />

      <VisibilityGroupPrompt
        visible={Boolean(visibilityGroupIntent)}
        enabled={visibilityGroupPromptEnabled}
        sending={confirmingVisibilityGroup}
        onToggle={() => setVisibilityGroupPromptEnabled((value) => !value)}
        onCancel={closeVisibilityGroupPrompt}
        onConfirm={confirmVisibilityGroupPrompt}
        colors={colors}
        styles={styles}
      />

      <CreateGroupModal
        visible={createGroupOpen}
        creating={creatingGroup}
        name={groupName}
        vibe={groupVibe}
        customGoal={groupCustomGoal}
        ageRange={groupAgeRange}
        maxMembers={groupSize}
        durationMinutes={groupDuration}
        radiusM={groupRadiusM}
        prompt={groupPrompt}
        onChangeName={setGroupName}
        onChangeVibe={setGroupVibe}
        onChangeCustomGoal={setGroupCustomGoal}
        onChangeAgeRange={setGroupAgeRange}
        onChangeMaxMembers={setGroupSize}
        onChangeDuration={setGroupDuration}
        onChangeRadius={setGroupRadiusM}
        onChangePrompt={setGroupPrompt}
        onCancel={() => setCreateGroupOpen(false)}
        onCreate={onCreateGroup}
        colors={colors}
        styles={styles}
      />

      <JoinGroupModal
        group={joinTarget}
        joining={Boolean(joiningGroupId)}
        onCancel={() => setJoinTarget(null)}
        onJoin={(allowMemberCues) => void confirmJoinGroup(allowMemberCues)}
        colors={colors}
        styles={styles}
      />
    </SafeAreaView>
  );
}

function VisibilityCuePrompt({
  visible,
  enabled,
  sending,
  personName,
  onToggle,
  onCancel,
  onConfirm,
  colors,
  styles,
}: {
  visible: boolean;
  enabled: boolean;
  sending: boolean;
  personName: string;
  onToggle: () => void;
  onCancel: () => void;
  onConfirm: () => void;
  colors: HomeColors;
  styles: HomeStyles;
}) {
  const { t } = useTranslation();
  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onCancel}
    >
      <View style={styles.cuePromptRoot}>
        <BlurredBackdrop
          accessibilityLabel={t(homeStrings.cancelVisibilityA11y)}
          disabled={sending}
          onPress={onCancel}
          style={styles.cuePromptBackdrop}
        />

        <View style={styles.cuePromptCard}>
          <View style={styles.cuePromptHeader}>
            <View style={styles.cuePromptIcon}>
              <Ionicons name="eye-outline" size={23} color={colors.primary} />
            </View>

            <View style={styles.cuePromptCopy}>
              <Text style={styles.cuePromptTitle}>
                {t(homeStrings.becomeVisibleFirst)}
              </Text>

              <Text style={styles.cuePromptDescription}>
                {t(homeStrings.visibilityCueBody, { name: personName })}
              </Text>
            </View>
          </View>

          <Pressable
            accessibilityRole="switch"
            accessibilityState={{ checked: enabled }}
            disabled={sending}
            onPress={onToggle}
            style={({ pressed }) => [
              styles.cuePromptToggleRow,
              pressed && styles.cuePromptToggleRowPressed,
            ]}
          >
            <View style={styles.cuePromptToggleCopy}>
              <Text style={styles.cuePromptToggleTitle}>
                {t(homeStrings.visible)}
              </Text>
              <Text style={styles.cuePromptToggleDescription}>
                {t(homeStrings.visibilityCueHint)}
              </Text>
            </View>

            <CompactSwitch enabled={enabled} disabled={sending} />
          </Pressable>

          <View style={styles.cuePromptActions}>
            <Pressable
              accessibilityRole="button"
              disabled={sending}
              onPress={onCancel}
              style={({ pressed }) => [
                styles.cuePromptButton,
                styles.cuePromptCancelButton,
                pressed && styles.cuePromptButtonPressed,
              ]}
            >
              <Text style={styles.cuePromptCancelText}>
                {t(homeStrings.cancel)}
              </Text>
            </Pressable>

            <Pressable
              accessibilityRole="button"
              disabled={sending || !enabled}
              onPress={onConfirm}
              style={({ pressed }) => [
                styles.cuePromptButton,
                styles.cuePromptOkButton,
                (sending || !enabled) && styles.buttonDisabled,
                pressed && styles.cuePromptButtonPressed,
              ]}
            >
              {sending ? (
                <ActivityIndicator color={colors.white} />
              ) : (
                <Text style={styles.cuePromptOkText}>
                  {t(homeStrings.visibilityConfirm)}
                </Text>
              )}
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
}

function VisibilityGroupPrompt({
  visible,
  enabled,
  sending,
  onToggle,
  onCancel,
  onConfirm,
  colors,
  styles,
}: {
  visible: boolean;
  enabled: boolean;
  sending: boolean;
  onToggle: () => void;
  onCancel: () => void;
  onConfirm: () => void;
  colors: HomeColors;
  styles: HomeStyles;
}) {
  const { t } = useTranslation();
  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onCancel}
    >
      <View style={styles.cuePromptRoot}>
        <BlurredBackdrop
          accessibilityLabel={t(homeStrings.cancelVisibilityA11y)}
          disabled={sending}
          onPress={onCancel}
          style={styles.cuePromptBackdrop}
        />

        <View style={styles.cuePromptCard}>
          <View style={styles.cuePromptHeader}>
            <View style={styles.cuePromptIcon}>
              <Ionicons
                name="people-outline"
                size={23}
                color={colors.primary}
              />
            </View>

            <View style={styles.cuePromptCopy}>
              <Text style={styles.cuePromptTitle}>
                {t(homeStrings.becomeVisibleFirst)}
              </Text>

              <Text style={styles.cuePromptDescription}>
                {t(homeStrings.visibilityGroupBody)}
              </Text>
            </View>
          </View>

          <Pressable
            accessibilityRole="switch"
            accessibilityState={{ checked: enabled }}
            disabled={sending}
            onPress={onToggle}
            style={({ pressed }) => [
              styles.cuePromptToggleRow,
              pressed && styles.cuePromptToggleRowPressed,
            ]}
          >
            <View style={styles.cuePromptToggleCopy}>
              <Text style={styles.cuePromptToggleTitle}>
                {t(homeStrings.visible)}
              </Text>
              <Text style={styles.cuePromptToggleDescription}>
                {t(homeStrings.visibilityGroupHint)}
              </Text>
            </View>

            <CompactSwitch enabled={enabled} disabled={sending} />
          </Pressable>

          <View style={styles.cuePromptActions}>
            <Pressable
              accessibilityRole="button"
              disabled={sending}
              onPress={onCancel}
              style={({ pressed }) => [
                styles.cuePromptButton,
                styles.cuePromptCancelButton,
                pressed && styles.cuePromptButtonPressed,
              ]}
            >
              <Text style={styles.cuePromptCancelText}>
                {t(homeStrings.cancel)}
              </Text>
            </Pressable>

            <Pressable
              accessibilityRole="button"
              disabled={sending || !enabled}
              onPress={onConfirm}
              style={({ pressed }) => [
                styles.cuePromptButton,
                styles.cuePromptOkButton,
                (sending || !enabled) && styles.buttonDisabled,
                pressed && styles.cuePromptButtonPressed,
              ]}
            >
              {sending ? (
                <ActivityIndicator color={colors.white} />
              ) : (
                <Text style={styles.cuePromptOkText}>
                  {t(homeStrings.visibilityConfirm)}
                </Text>
              )}
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
}

function VenueCard({
  name,
  named,
  joinedSpaceName,
  onEdit,
  colors,
  styles,
}: {
  name: string;
  named: boolean;
  /**
   * The space the user explicitly JOINED for Same Space, if any.
   *
   * Changes the sentence, not just the word. "Near X" exists because the
   * resolver snaps to the closest named place and is routinely a few dozen
   * metres off — it is a hedge on OUR guess. A joined space is not a guess: the
   * person picked it by name, having been proved within the join radius. So it
   * is shown plainly, and the hedge goes back the moment they clear it.
   */
  joinedSpaceName: string | null;
  onEdit: () => void;
  colors: HomeColors;
  styles: HomeStyles;
}) {
  const { t } = useTranslation();
  return (
    <View style={styles.venueCard}>
      <View style={styles.venueCardTop}>
        <View style={styles.venueIcon}>
          <Ionicons name="location" size={28} color={colors.primary} />
        </View>

        <View style={styles.venueHeading}>
          <Text style={styles.venueLabel}>{t(homeStrings.currentSpace)}</Text>

          <View style={styles.venueTitleRow}>
            {/*
              Presented as "Near X", never "X". Detection snaps to the closest
              named place, which is routinely a few dozen metres away — saying
              "Near" is both honest and stops users reading an imprecise guess
              as us getting their location wrong.

              And only when there IS an X. A space whose name has not resolved
              yet is real — people are grouped and cued in it — but it has no
              name to be near, and this template used to be handed the
              placeholder regardless. When that placeholder still carried the
              cell's coordinates the screen read "Near Nearby space 53.445,
              -6.227": the user's own position, printed as the name of where
              they are, by the very feature that exists to avoid naming it.
            */}
            <Text numberOfLines={2} style={styles.venueName}>
              {joinedSpaceName
                ? joinedSpaceName
                : named
                  ? t(homeStrings.nearSpace, { name })
                  : t(homeStrings.unnamedSpace)}
            </Text>

            {/* "Join a space" until there is one, then "Change space" — the
                same word the filter sheet uses for the same act, because it
                opens the same screen and has the same outcome. Two labels for
                one action is how the screen came to describe two spaces. */}
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t(
                joinedSpaceName
                  ? homeStrings.sameSpaceJoin
                  : homeStrings.editLocation,
              )}
              onPress={onEdit}
              style={({ pressed }) => [
                styles.editLocationButton,
                pressed && styles.editLocationButtonPressed,
              ]}
            >
              <Ionicons
                name="pencil-outline"
                size={17}
                color={colors.primary}
              />

              <Text style={styles.editLocationText}>
                {t(
                  joinedSpaceName
                    ? homeStrings.sameSpaceJoin
                    : homeStrings.editLocation,
                )}
              </Text>
            </Pressable>
          </View>
        </View>
      </View>
    </View>
  );
}

function PanelTabs({
  activePanel,
  onChange,
  peopleFilterCount,
  groupFilterCount,
  onOpenPeopleFilters,
  onOpenGroupFilters,
  colors,
  styles,
}: {
  activePanel: HomePanel;
  onChange: (panel: HomePanel) => void;
  peopleFilterCount: number;
  groupFilterCount: number;
  onOpenPeopleFilters: () => void;
  onOpenGroupFilters: () => void;
  colors: HomeColors;
  styles: HomeStyles;
}) {
  const { t } = useTranslation();
  return (
    <View style={styles.panelTabs}>
      <PanelTab
        label={t(homeStrings.peoplePanel)}
        active={activePanel === "people"}
        filterCount={peopleFilterCount}
        filterLabel={t(homeStrings.filterPeopleA11y)}
        onSelect={() => onChange("people")}
        onOpenFilters={onOpenPeopleFilters}
        colors={colors}
        styles={styles}
      />

      <PanelTab
        label={t(homeStrings.groupsPanel)}
        active={activePanel === "groups"}
        filterCount={groupFilterCount}
        filterLabel={t(homeStrings.filterGroupsTitle)}
        onSelect={() => onChange("groups")}
        onOpenFilters={onOpenGroupFilters}
        colors={colors}
        styles={styles}
      />
    </View>
  );
}

// The inline filter lives on whichever tab is active, so it always targets the
// panel the person is currently looking at.
function PanelTab({
  label,
  active,
  filterCount,
  filterLabel,
  onSelect,
  onOpenFilters,
  colors,
  styles,
}: {
  label: string;
  active: boolean;
  filterCount: number;
  filterLabel: string;
  onSelect: () => void;
  onOpenFilters: () => void;
  colors: HomeColors;
  styles: HomeStyles;
}) {
  const { t } = useTranslation();
  if (!active) {
    return (
      <Pressable
        accessibilityRole="tab"
        accessibilityState={{ selected: false }}
        onPress={onSelect}
        style={({ pressed }) => [
          styles.panelTab,
          pressed && styles.panelTabPressed,
        ]}
      >
        <Text numberOfLines={1} style={styles.panelTabText}>
          {label}
        </Text>
      </Pressable>
    );
  }

  return (
    <View style={[styles.panelCompositeButton, styles.panelTabActive]}>
      <Pressable
        accessibilityRole="tab"
        accessibilityState={{ selected: true }}
        onPress={onSelect}
        style={({ pressed }) => [
          styles.panelCompositeSegment,
          pressed && styles.panelTabPressed,
        ]}
      >
        <Text
          numberOfLines={1}
          style={[styles.panelTabText, styles.panelTabTextActive]}
        >
          {label}
        </Text>
      </Pressable>

      <Text style={styles.panelInnerSeparator}>|</Text>

      <Pressable
        accessibilityRole="button"
        accessibilityLabel={filterLabel}
        onPress={onOpenFilters}
        style={({ pressed }) => [
          styles.panelCompositeFilter,
          filterCount > 0 && styles.panelCompositeFilterActive,
          pressed && styles.panelTabPressed,
        ]}
      >
        <Ionicons
          name="filter-outline"
          size={14}
          color={filterCount > 0 ? colors.white : colors.primary}
        />
        <Text
          numberOfLines={1}
          style={[
            styles.inlineFilterText,
            filterCount > 0 && styles.inlineFilterTextActive,
          ]}
        >
          {filterCount > 0 ? `${filterCount}` : t(homeStrings.filter)}
        </Text>
      </Pressable>
    </View>
  );
}

function PeopleFilterModal({
  visible,
  filters,
  nearbyRadiusM,
  locks,
  options,
  onChange,
  onNearbyRadiusChange,
  nearbyMode,
  joinedSpaceName,
  onSelectSameSpace,
  onChangeSpace,
  onSelectNearby,
  onClear,
  onClose,
  colors,
  styles,
}: {
  visible: boolean;
  filters: PeopleFilters;
  nearbyRadiusM: number;
  /** Which list is being read. `null` when Same Space cannot be offered. */
  nearbyMode: NearbyMode;
  /** The space actually joined, or null while the radius list is showing. */
  joinedSpaceName: string | null;
  onSelectSameSpace: () => void;
  onChangeSpace: () => void;
  onSelectNearby: () => void;
  locks: FilterLocks;
  options: {
    languages: string[];
    friendshipGoals: string[];
  };
  onChange: (filters: PeopleFilters) => void | Promise<boolean>;
  // Returns the save's promise so the slider can hold the released
  // position until the write settles (see nearby-radius-slider.tsx).
  onNearbyRadiusChange: (radiusM: number) => Promise<void>;
  onClear: () => void;
  onClose: () => void;
  colors: HomeColors;
  styles: HomeStyles;
}) {
  const { t } = useTranslation();
  function setFilter<K extends keyof PeopleFilters>(
    key: K,
    value: PeopleFilters[K],
  ) {
    // Returned, not discarded — the age slider holds its thumb on this.
    return onChange({
      ...filters,
      [key]: value,
    });
  }

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <View style={styles.cuePromptRoot}>
        <BlurredBackdrop
          accessibilityLabel={t(homeStrings.closePeopleA11y)}
          onPress={onClose}
          style={styles.cuePromptBackdrop}
        />

        <View style={styles.peopleFilterSheet}>
          <View style={styles.peopleFilterSheetHeader}>
            <View style={styles.cuePromptIcon}>
              <Ionicons
                name="filter-outline"
                size={22}
                color={colors.primary}
              />
            </View>
            <View style={styles.cuePromptCopy}>
              <Text style={styles.cuePromptTitle}>
                {t(homeStrings.filterPeopleTitle)}
              </Text>
              <Text style={styles.cuePromptDescription}>
                {t(homeStrings.filterPeopleBody)}
              </Text>
            </View>
          </View>

          <ScrollView showsVerticalScrollIndicator={false}>
            {/* Mode first, then the range — and the range only when the mode
                is still using it. See `NearbyModeToggle` for why the slider is
                hidden rather than disabled in Same Space. */}
            {/* ALWAYS offered. It used to render only when the CURRENT venue
                was a real place, which quietly inverted the feature: the
                people who most need Same Space are exactly the ones the
                resolver has put on a grid cell or the wrong business, and they
                were the ones who could not reach the control that lets them
                say so. Whether a chosen space is joinable is the server's
                call, on the space the user actually names. */}
            <View style={styles.peopleFilterSection}>
              <Text style={styles.circleFieldLabel}>
                {t(homeStrings.whoToShow)}
              </Text>
              <NearbyModeToggle
                mode={nearbyMode}
                joinedSpaceName={joinedSpaceName}
                onSelectSameSpace={onSelectSameSpace}
                onChangeSpace={onChangeSpace}
                onSelectNearby={onSelectNearby}
                colors={{
                  accent: colors.primary,
                  accentSoft: colors.primarySoft,
                  border: colors.border,
                  label: colors.textPrimary,
                  labelMuted: colors.textSecondary,
                  surface: colors.surface,
                }}
              />
            </View>
            {nearbyMode === "radius" ? (
              <View style={styles.peopleFilterSection}>
                <Text style={styles.circleFieldLabel}>
                  {t(homeStrings.nearbyRange)}
                </Text>
                <NearbyRadiusSlider
                  valueM={nearbyRadiusM}
                  onCommit={onNearbyRadiusChange}
                  colors={{
                    track: colors.primary,
                    trackRemaining: colors.border,
                    thumb: colors.primary,
                    label: colors.textPrimary,
                    labelMuted: colors.textSecondary,
                  }}
                />
              </View>
            ) : null}
            <AgeRangeFilterSection
              title={t(homeStrings.ageRange)}
              selected={filters.ageBand}
              lockedMessage={locks.ageBand}
              onChange={(value) => setFilter("ageBand", value)}
              colors={colors}
              styles={styles}
            />
            <FilterSection
              title={t(homeStrings.friendshipGoal)}
              placeholder={t(homeStrings.any)}
              options={options.friendshipGoals}
              selected={filters.friendshipGoal}
              lockedMessage={locks.friendshipGoal}
              labelFor={goalLabel}
              onChange={(value) => setFilter("friendshipGoal", value)}
              colors={colors}
              styles={styles}
            />
            <FilterSection
              title={t(homeStrings.languages)}
              placeholder={t(homeStrings.any)}
              options={options.languages}
              selected={filters.language}
              lockedMessage={locks.language}
              labelFor={languageLabel}
              onChange={(value) => setFilter("language", value)}
              colors={colors}
              styles={styles}
            />
          </ScrollView>

          <View style={styles.cuePromptActions}>
            <Pressable
              accessibilityRole="button"
              onPress={onClear}
              style={({ pressed }) => [
                styles.cuePromptButton,
                styles.cuePromptCancelButton,
                pressed && styles.cuePromptButtonPressed,
              ]}
            >
              <Text style={styles.cuePromptCancelText}>
                {t(homeStrings.clear)}
              </Text>
            </Pressable>

            <Pressable
              accessibilityRole="button"
              onPress={onClose}
              style={({ pressed }) => [
                styles.cuePromptButton,
                styles.cuePromptOkButton,
                pressed && styles.cuePromptButtonPressed,
              ]}
            >
              <Text style={styles.cuePromptOkText}>{t(homeStrings.done)}</Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
}

function GroupFilterModal({
  visible,
  filters,
  nearbyRadiusM,
  onChange,
  onNearbyRadiusChange,
  onClear,
  onClose,
  colors,
  styles,
}: {
  visible: boolean;
  filters: GroupFilters;
  nearbyRadiusM: number;
  onChange: (filters: GroupFilters) => void;
  // Returns the save's promise so the slider can hold the released
  // position until the write settles (see nearby-radius-slider.tsx).
  onNearbyRadiusChange: (radiusM: number) => Promise<void>;
  onClear: () => void;
  onClose: () => void;
  colors: HomeColors;
  styles: HomeStyles;
}) {
  const { t } = useTranslation();
  function setFilter(key: keyof GroupFilters, value: string | null) {
    onChange({
      ...filters,
      [key]: value,
    });
  }

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <View style={styles.cuePromptRoot}>
        <BlurredBackdrop
          accessibilityLabel={t(homeStrings.closeGroupsA11y)}
          onPress={onClose}
          style={styles.cuePromptBackdrop}
        />

        <View style={styles.peopleFilterSheet}>
          <View style={styles.peopleFilterSheetHeader}>
            <View style={styles.cuePromptIcon}>
              <Ionicons
                name="filter-outline"
                size={22}
                color={colors.primary}
              />
            </View>
            <View style={styles.cuePromptCopy}>
              <Text style={styles.cuePromptTitle}>
                {t(homeStrings.filterGroupsTitle)}
              </Text>
              <Text style={styles.cuePromptDescription}>
                {t(homeStrings.filterGroupsBody)}
              </Text>
            </View>
          </View>

          <ScrollView showsVerticalScrollIndicator={false}>
            {/* Same per-user radius as the People filter — the server scopes
                the group query with it, so the two sheets edit one setting. */}
            <View style={styles.peopleFilterSection}>
              <Text style={styles.circleFieldLabel}>
                {t(homeStrings.nearbyRange)}
              </Text>
              <NearbyRadiusSlider
                valueM={nearbyRadiusM}
                onCommit={onNearbyRadiusChange}
                colors={{
                  track: colors.primary,
                  trackRemaining: colors.border,
                  thumb: colors.primary,
                  label: colors.textPrimary,
                  labelMuted: colors.textSecondary,
                }}
              />
            </View>
            <FilterSection
              title={t(homeStrings.goal)}
              placeholder={t(homeStrings.any)}
              options={GROUP_GOALS}
              selected={filters.vibe}
              labelFor={groupGoalLabel}
              onChange={(value) => setFilter("vibe", value)}
              colors={colors}
              styles={styles}
            />
            <AgeRangeFilterSection
              title={t(homeStrings.ageRange)}
              selected={filters.ageRange}
              onChange={(value) => setFilter("ageRange", value)}
              colors={colors}
              styles={styles}
            />
            <FilterSection
              title={t(homeStrings.groupSize)}
              placeholder={t(homeStrings.any)}
              options={GROUP_SIZES.map(String)}
              selected={filters.size}
              labelFor={(value) =>
                t(homeStrings.peopleOption, { count: Number(value) })
              }
              onChange={(value) => setFilter("size", value)}
              colors={colors}
              styles={styles}
            />
          </ScrollView>

          <View style={styles.cuePromptActions}>
            <Pressable
              accessibilityRole="button"
              onPress={onClear}
              style={({ pressed }) => [
                styles.cuePromptButton,
                styles.cuePromptCancelButton,
                pressed && styles.cuePromptButtonPressed,
              ]}
            >
              <Text style={styles.cuePromptCancelText}>
                {t(homeStrings.clear)}
              </Text>
            </Pressable>

            <Pressable
              accessibilityRole="button"
              onPress={onClose}
              style={({ pressed }) => [
                styles.cuePromptButton,
                styles.cuePromptOkButton,
                pressed && styles.cuePromptButtonPressed,
              ]}
            >
              <Text style={styles.cuePromptOkText}>{t(homeStrings.done)}</Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
}

function AgeRangeFilterSection({
  title,
  selected,
  lockedMessage,
  onChange,
  colors,
  styles,
}: {
  title: string;
  selected: string | null;
  lockedMessage?: string | null;
  onChange: (value: string | null) => void | Promise<boolean>;
  colors: HomeColors;
  styles: HomeStyles;
}) {
  return (
    <View style={styles.peopleFilterSection}>
      <Text style={styles.circleFieldLabel}>{title}</Text>
      {lockedMessage ? (
        <View style={styles.peopleFilterLocked}>
          <Ionicons
            name="lock-closed-outline"
            size={14}
            color={colors.textMuted}
          />
          <Text style={styles.peopleFilterLockedText}>{lockedMessage}</Text>
        </View>
      ) : (
        <AgeRangeSlider
          selected={selected}
          onChange={onChange}
          colors={{
            track: colors.primary,
            trackRemaining: colors.border,
            thumb: colors.primary,
            label: colors.textPrimary,
            labelMuted: colors.textSecondary,
          }}
        />
      )}
    </View>
  );
}

function FilterSection({
  title,
  placeholder,
  options,
  selected,
  lockedMessage,
  onChange,
  colors,
  styles,
  labelFor = (value: string) => value,
  descriptionFor,
}: {
  title: string;
  placeholder: string;
  options: string[];
  selected: string | null;
  lockedMessage?: string | null;
  onChange: (value: string | null) => void;
  colors: HomeColors;
  styles: HomeStyles;
  labelFor?: (value: string) => string;
  descriptionFor?: (value: string) => string;
}) {
  return (
    <View style={styles.peopleFilterSection}>
      <Text style={styles.circleFieldLabel}>{title}</Text>
      {lockedMessage ? (
        <View style={styles.peopleFilterLocked}>
          <Ionicons
            name="lock-closed-outline"
            size={14}
            color={colors.textMuted}
          />
          <Text style={styles.peopleFilterLockedText}>{lockedMessage}</Text>
        </View>
      ) : (
        <FilterSelect
          placeholder={placeholder}
          options={options}
          selected={selected}
          onChange={onChange}
          labelFor={labelFor}
          descriptionFor={descriptionFor}
          colors={colors}
          styles={styles}
        />
      )}
    </View>
  );
}

/**
 * A fully theme-controlled dropdown. The native Picker renders its popup on a
 * system-white surface, which makes our light text invisible in dark mode — so
 * we drive our own trigger + option sheet instead.
 */
function FilterSelect({
  placeholder,
  options,
  selected,
  onChange,
  labelFor,
  descriptionFor,
  colors,
  styles,
}: {
  placeholder: string;
  options: string[];
  selected: string | null;
  onChange: (value: string | null) => void;
  labelFor: (value: string) => string;
  descriptionFor?: (value: string) => string;
  colors: HomeColors;
  styles: HomeStyles;
}) {
  const [open, setOpen] = useState(false);
  const selectedLabel = selected ? labelFor(selected) : null;

  function choose(value: string | null) {
    onChange(value);
    setOpen(false);
  }

  return (
    <>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={selectedLabel ?? placeholder}
        onPress={() => setOpen(true)}
        style={styles.peopleFilterPickerShell}
      >
        <Text
          numberOfLines={1}
          style={[styles.peopleFilterSelectText, { color: colors.textPrimary }]}
        >
          {selectedLabel ?? placeholder}
        </Text>
        <Ionicons name="chevron-down" size={16} color={colors.textMuted} />
      </Pressable>

      <Modal
        visible={open}
        transparent
        animationType="fade"
        onRequestClose={() => setOpen(false)}
      >
        <BlurredBackdrop
          style={styles.filterSelectBackdrop}
          onPress={() => setOpen(false)}
        />
        <View style={styles.filterSelectCenter} pointerEvents="box-none">
          <View
            style={[
              styles.filterSelectSheet,
              { backgroundColor: colors.surface, borderColor: colors.border },
            ]}
          >
            <ScrollView
              showsVerticalScrollIndicator
              persistentScrollbar
              keyboardShouldPersistTaps="handled"
            >
              <FilterOption
                label={placeholder}
                active={!selected}
                onPress={() => choose(null)}
                colors={colors}
                styles={styles}
                muted
              />
              {options.map((option) => (
                <FilterOption
                  key={option}
                  label={labelFor(option)}
                  description={descriptionFor?.(option)}
                  active={selected === option}
                  onPress={() => choose(option)}
                  colors={colors}
                  styles={styles}
                />
              ))}
            </ScrollView>
          </View>
        </View>
      </Modal>
    </>
  );
}

function FilterOption({
  label,
  description,
  active,
  onPress,
  colors,
  styles,
  muted = false,
}: {
  label: string;
  /** Optional second line saying what the choice is for. */
  description?: string;
  active: boolean;
  onPress: () => void;
  colors: HomeColors;
  styles: HomeStyles;
  muted?: boolean;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ selected: active }}
      // Read as one phrase, so the reason is announced with the option rather
      // than as a stray second element after it.
      accessibilityLabel={description ? `${label}. ${description}` : label}
      onPress={onPress}
      style={[
        styles.filterSelectOption,
        description != null && styles.filterSelectOptionStacked,
        { borderBottomColor: colors.border },
      ]}
    >
      <View style={styles.filterSelectOptionCopy}>
        <Text
          style={[
            styles.filterSelectOptionText,
            {
              color: muted
                ? colors.textPrimary
                : active
                  ? colors.primary
                  : colors.textPrimary,
            },
          ]}
        >
          {label}
        </Text>
        {description ? (
          <Text
            style={[
              styles.filterSelectOptionDescription,
              { color: colors.textMuted },
            ]}
          >
            {description}
          </Text>
        ) : null}
      </View>
      {active ? (
        <Ionicons name="checkmark" size={18} color={colors.primary} />
      ) : null}
    </Pressable>
  );
}

/**
 * Title for the steps dialog, matched to what is actually standing in the way.
 * Only the states `getLocationAccessSteps` writes steps for can reach this.
 */
function locationStepsTitle(
  access: LocationAccess,
  t: ReturnType<typeof useTranslation>["t"],
): string {
  switch (access) {
    case "permission-blocked":
      return t(locationStrings.blockedWebTitle, {
        browser: getWebEnvironment().browserName,
      });
    case "services-disabled":
      return t(locationStrings.offTitle);
    case "web-insecure":
      return t(locationStrings.secureTitle);
    case "web-unsupported":
      return t(locationStrings.unsupportedTitle);
    default:
      return t(locationStrings.errorTitle);
  }
}

/** The failures a coarser-than-precise fix could plausibly be behind. */
function isLocationError(error: NearbyError): boolean {
  return (
    error === "permission_denied" ||
    error === "device_location_off" ||
    error === "location_timeout" ||
    // Both refusal states: an approximate grant is vaguer AND refreshed less
    // often, so a precision upgrade is the plausible remedy for either.
    error === "location_stale" ||
    error === "location_imprecise"
  );
}

function nearbyErrorCopy(error: NearbyError) {
  switch (error) {
    case "offline":
      return {
        title: homeStrings.offlineTitle,
        body: homeStrings.offlineBody,
        button: homeStrings.retryConnection,
        a11y: homeStrings.retryConnectionA11y,
        icon: "cloud-offline-outline" as const,
      };
    case "location_imprecise":
      // Current, but too vague for the range they chose. Waiting does not help
      // and neither does re-detecting; what helps is a precise grant or a wider
      // range, which is what this copy and the Allow-precise action offer.
      return {
        title: homeStrings.peoplePreciseTitle,
        body: homeStrings.peoplePreciseBody,
        button: homeStrings.retryLocation,
        a11y: homeStrings.retryLocationA11y,
        icon: "navigate-outline" as const,
      };
    case "location_stale":
      // Location works and permission is granted — the fix we hold is simply
      // too old or too vague to publish. So the copy asks for a refresh rather
      // than sending the user to their settings to fix something that is not
      // broken, and the button re-reads position instead of retrying a request.
      return {
        title: homeStrings.refreshTitle,
        body: homeStrings.refreshBody,
        button: homeStrings.retryLocation,
        a11y: homeStrings.retryLocationA11y,
        icon: "navigate-outline" as const,
      };
    case "permission_denied":
    case "device_location_off":
    case "location_timeout":
      return {
        title: homeStrings.locationFailedTitle,
        body: homeStrings.locationFailedBody,
        button: homeStrings.retryLocation,
        a11y: homeStrings.retryLocationA11y,
        icon: "navigate-outline" as const,
      };
    case "backend_unavailable":
      return {
        title: homeStrings.backendUnavailableTitle,
        body: homeStrings.backendUnavailableBody,
        button: homeStrings.retryConnection,
        a11y: homeStrings.retryConnectionA11y,
        icon: "cloud-offline-outline" as const,
      };
    case "request_failed":
      return {
        title: homeStrings.requestFailedTitle,
        body: homeStrings.requestFailedBody,
        button: homeStrings.retryConnection,
        a11y: homeStrings.retryConnectionA11y,
        icon: "alert-circle-outline" as const,
      };
  }
}

/**
 * Shown when the nearby/space refresh resolved to a specific failure.
 *
 * Not a loading state: while location or network requests are still resolving
 * the screen shows a plain spinner instead, so this never appears without a way
 * out of it.
 */
function NearbyErrorState({
  error,
  onRetry,
  onAcknowledge,
  onAllowPrecise,
  colors,
  styles,
}: {
  error: NearbyError;
  onRetry: () => void;
  /**
   * Dismiss the block for a failure no retry can change.
   *
   * Used in place of `onRetry` for `location_imprecise`, where the next reading
   * comes from the same coarse grant and lands in the same place — so a Try
   * again button there is a loop the user is invited to run by hand. The
   * remedies are the button above (a precise grant) or a wider range; this one
   * only stops the screen holding them there.
   */
  onAcknowledge: () => void;
  /**
   * Present only when the grant is approximate and that is a plausible reason
   * this failed. The system's own upgrade dialog explains nothing, so it is
   * never reached except through this button, under the paragraph below.
   */
  onAllowPrecise?: () => void;
  colors: HomeColors;
  styles: HomeStyles;
}) {
  const { t } = useTranslation();
  const copy = nearbyErrorCopy(error);
  const offerPrecise = Boolean(onAllowPrecise) && isLocationError(error);
  // Retrying an imprecise fix re-runs the coarse grant and returns the same
  // answer. Nothing about waiting or tapping changes it, so the trailing button
  // stops offering to.
  const retryIsFutile = error === "location_imprecise";
  return (
    <View style={styles.noVenueState}>
      <View style={styles.noVenueGraphic}>
        <View style={styles.noVenueOuterRing} />

        <View style={styles.noVenueInnerRing} />

        <View style={styles.noVenueIcon}>
          <Ionicons name={copy.icon} size={29} color={colors.white} />
        </View>
      </View>

      <View style={styles.noVenueCopy}>
        <Text style={styles.noVenueTitle}>
          {t(offerPrecise ? homeStrings.preciseTitle : copy.title)}
        </Text>

        <Text style={styles.noVenueDescription}>
          {t(offerPrecise ? homeStrings.preciseBody : copy.body)}
        </Text>
      </View>

      {offerPrecise ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t(homeStrings.preciseActionA11y)}
          onPress={onAllowPrecise}
          style={({ pressed }) => [
            styles.secondaryButton,
            pressed && styles.secondaryButtonPressed,
          ]}
        >
          <Ionicons
            name="location-outline"
            size={19}
            color={colors.primaryStrong}
          />

          <Text style={styles.secondaryButtonText}>
            {t(homeStrings.preciseAction)}
          </Text>
        </Pressable>
      ) : null}

      <Pressable
        accessibilityRole="button"
        accessibilityLabel={t(
          retryIsFutile ? homeStrings.continueAnywayA11y : copy.a11y,
        )}
        onPress={retryIsFutile ? onAcknowledge : onRetry}
        style={({ pressed }) => [
          styles.secondaryButton,
          pressed && styles.secondaryButtonPressed,
        ]}
      >
        <Ionicons
          name={retryIsFutile ? "arrow-forward-outline" : "refresh-outline"}
          size={19}
          color={colors.primaryStrong}
        />

        <Text style={styles.secondaryButtonText}>
          {t(retryIsFutile ? homeStrings.continueAnyway : copy.button)}
        </Text>
      </Pressable>
    </View>
  );
}

function ShareInviteCard({
  onShare,
  colors,
  styles,
}: {
  onShare: () => void;
  colors: HomeColors;
  styles: HomeStyles;
}) {
  const { t } = useTranslation();
  return (
    <View style={styles.shareCard}>
      <View style={styles.shareCardTop}>
        <View style={styles.shareIconWrap}>
          <Ionicons
            name="share-social-outline"
            size={22}
            color={colors.primary}
          />
        </View>

        <View style={styles.shareCopy}>
          <Text style={styles.shareTitle}>{t(homeStrings.shareCardTitle)}</Text>

          <Text style={styles.shareDescription}>
            {t(homeStrings.shareCardBody)}
          </Text>
        </View>
      </View>

      <Pressable
        accessibilityRole="button"
        accessibilityLabel={t(homeStrings.shareButton)}
        onPress={onShare}
        style={({ pressed }) => [
          styles.shareButton,
          pressed && styles.shareButtonPressed,
        ]}
      >
        <Ionicons name="share-social-outline" size={18} color={colors.white} />

        <Text style={styles.shareButtonText}>{t(homeStrings.shareButton)}</Text>
      </Pressable>
    </View>
  );
}

function SmallGroupsPanel({
  groups,
  loading,
  filterActive,
  onClearFilters,
  onCreate,
  onJoin,
  colors,
  styles,
}: {
  groups: SmallGroup[];
  loading: boolean;
  filterActive: boolean;
  onClearFilters: () => void;
  onCreate: () => void;
  onJoin: (group: SmallGroup) => void;
  colors: HomeColors;
  styles: HomeStyles;
}) {
  const { t } = useTranslation();
  return (
    <View style={styles.circlePanel}>
      <View style={styles.circleHero}>
        <Text style={styles.circleHeroDescription}>
          {t(homeStrings.groupsIntro)}
        </Text>
      </View>

      <View style={styles.circleHeroActions}>
        <Pressable
          accessibilityRole="button"
          onPress={onCreate}
          style={({ pressed }) => [
            styles.circlePrimaryButton,
            pressed && styles.circleButtonPressed,
          ]}
        >
          <Ionicons name="add" size={18} color={colors.white} />
          <Text style={styles.circlePrimaryButtonText}>
            {t(homeStrings.createGroup)}
          </Text>
        </Pressable>
      </View>

      {loading ? (
        <View style={styles.circleLoading}>
          <ActivityIndicator color={colors.primary} />
        </View>
      ) : groups.length ? (
        <View style={styles.circleList}>
          <Text style={styles.circleCountText}>
            {t(homeStrings.nearbyGroupsCount, { count: groups.length })}
          </Text>

          {groups.map((group) => (
            <GroupCard
              key={group.id}
              group={group}
              onPress={() => onJoin(group)}
              colors={colors}
              styles={styles}
            />
          ))}
        </View>
      ) : filterActive ? (
        <View style={styles.circleEmpty}>
          <Text style={styles.circleEmptyTitle}>
            {t(homeStrings.noGroupsMatch)}
          </Text>
          <Text style={styles.circleEmptyDescription}>
            {t(homeStrings.noGroupsMatchBody)}
          </Text>

          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t(homeStrings.closeGroupsA11y)}
            onPress={onClearFilters}
            style={({ pressed }) => [
              styles.emptyShareButton,
              pressed && styles.emptyShareButtonPressed,
            ]}
          >
            <Ionicons
              name="close-circle-outline"
              size={17}
              color={colors.primaryStrong}
            />
            <Text style={styles.emptyShareButtonText}>
              {t(homeStrings.clearFilters)}
            </Text>
          </Pressable>
        </View>
      ) : (
        <View style={styles.circleEmpty}>
          <Text style={styles.circleEmptyTitle}>{t(homeStrings.noGroups)}</Text>
          <Text style={styles.circleEmptyDescription}>
            {t(homeStrings.noGroupsBody)}
          </Text>
        </View>
      )}
    </View>
  );
}

function GroupCard({
  group,
  onPress,
  colors,
  styles,
}: {
  group: SmallGroup;
  onPress: () => void;
  colors: HomeColors;
  styles: HomeStyles;
}) {
  const { t } = useTranslation();
  const full = group.memberCount >= group.maxMembers && !group.isMember;
  const statusLabel = t(
    group.isMember
      ? homeStrings.groupOpen
      : full
        ? homeStrings.groupFull
        : homeStrings.groupJoinAction,
  );
  const preview = group.prompt?.trim() || groupGoalLabel(group.vibe);
  const members = group.members.map((member) => member.displayName).join(" · ");
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={t(
        group.isMember
          ? homeStrings.groupOpenA11y
          : full
            ? homeStrings.groupViewA11y
            : homeStrings.groupJoinA11y,
        { name: group.name },
      )}
      disabled={full}
      onPress={onPress}
      style={({ hovered, pressed }) => [
        styles.circleCard,
        Platform.OS === "web" && hovered && !full && styles.circleCardHovered,
        pressed && !full && styles.circleCardPressed,
        full && styles.circleCardDisabled,
      ]}
    >
      <View style={styles.circleGroupIcon}>
        <Ionicons name="people" size={24} color={colors.primary} />
      </View>

      <View style={styles.circleCardTitleWrap}>
        <Text style={styles.circleCardTitle} numberOfLines={1}>
          {group.name}
        </Text>

        <Text style={styles.circleCardMeta} numberOfLines={1}>
          {t(homeStrings.groupCardMeta, {
            memberCount: group.memberCount,
            maxMembers: group.maxMembers,
            timeLeft: formatCompactDuration(group.minutesLeft),
          })}
        </Text>

        <Text style={styles.groupPrompt} numberOfLines={1}>
          {preview}
        </Text>

        {members ? (
          <Text style={styles.circleMembers} numberOfLines={1}>
            {members}
          </Text>
        ) : null}
      </View>

      <View style={styles.circleRowMeta}>
        <Text
          style={[styles.circleRowStatus, full && styles.circleRowStatusMuted]}
        >
          {statusLabel}
        </Text>

        {!full ? (
          <Ionicons name="chevron-forward" size={17} color={colors.textMuted} />
        ) : null}
      </View>
    </Pressable>
  );
}

function CreateGroupModal({
  visible,
  creating,
  name,
  vibe,
  customGoal,
  ageRange,
  maxMembers,
  durationMinutes,
  radiusM,
  prompt,
  onChangeName,
  onChangeVibe,
  onChangeCustomGoal,
  onChangeAgeRange,
  onChangeMaxMembers,
  onChangeDuration,
  onChangeRadius,
  onChangePrompt,
  onCancel,
  onCreate,
  colors,
  styles,
}: {
  visible: boolean;
  creating: boolean;
  name: string;
  vibe: string;
  customGoal: string;
  ageRange: string;
  maxMembers: number;
  durationMinutes: number;
  radiusM: number;
  prompt: string;
  onChangeName: (value: string) => void;
  onChangeVibe: (value: string) => void;
  onChangeCustomGoal: (value: string) => void;
  onChangeAgeRange: (value: string) => void;
  onChangeMaxMembers: (value: number) => void;
  onChangeDuration: (value: number) => void;
  onChangeRadius: (value: number) => void;
  onChangePrompt: (value: string) => void;
  onCancel: () => void;
  onCreate: () => void;
  colors: HomeColors;
  styles: HomeStyles;
}) {
  const { t } = useTranslation();
  const isCustomGoal = vibe === OTHER_GROUP_GOAL;
  const canCreate =
    Boolean(name.trim()) && (!isCustomGoal || Boolean(customGoal.trim()));

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onCancel}
    >
      <View style={styles.cuePromptRoot}>
        <BlurredBackdrop
          accessibilityLabel={t(homeStrings.cancelCreateA11y)}
          disabled={creating}
          onPress={onCancel}
          style={styles.cuePromptBackdrop}
        />

        <KeyboardAvoidingContainer style={styles.modalKeyboardView}>
          <View style={styles.createGroupCard}>
            <ScrollView
              showsVerticalScrollIndicator={false}
              keyboardShouldPersistTaps="handled"
              contentContainerStyle={styles.createGroupScroll}
            >
              <Text style={styles.createGroupTitle}>
                {t(homeStrings.createGroupTitle)}
              </Text>
              <Text style={styles.createGroupDescription}>
                {t(homeStrings.createGroupBody)}
              </Text>

              <View style={styles.circleField}>
                <Text style={styles.circleFieldLabel}>
                  {t(homeStrings.groupName)}
                </Text>
                <TextInput
                  value={name}
                  onChangeText={onChangeName}
                  placeholder={t(homeStrings.groupNamePlaceholder)}
                  placeholderTextColor={colors.textMuted}
                  maxLength={20}
                  style={[
                    styles.groupTextInput,
                    { color: colors.textPrimary, borderColor: colors.border },
                  ]}
                />
              </View>

              <View style={styles.circleField}>
                <Text style={styles.circleFieldLabel}>{t(homeStrings.goal)}</Text>
                <FilterSelect
                  placeholder={t(homeStrings.selectGoal)}
                  options={GROUP_GOALS}
                  selected={vibe}
                  onChange={(value) => onChangeVibe(value ?? vibe)}
                  labelFor={groupGoalLabel}
                  colors={colors}
                  styles={styles}
                />

                {isCustomGoal ? (
                  <TextInput
                    value={customGoal}
                    onChangeText={onChangeCustomGoal}
                    placeholder={t(homeStrings.customGoalPlaceholder)}
                    placeholderTextColor={colors.textMuted}
                    maxLength={CUSTOM_GROUP_GOAL_MAX_LENGTH}
                    style={[
                      styles.groupTextInput,
                      { color: colors.textPrimary, borderColor: colors.border },
                    ]}
                  />
                ) : null}
              </View>

              <View style={styles.circleField}>
                <Text style={styles.circleFieldLabel}>
                  {t(homeStrings.ageRange)}
                </Text>
                <AgeRangeSlider
                  selected={
                    ageRange.toLowerCase() === ANY_AGE_RANGE_LABEL.toLowerCase()
                      ? null
                      : ageRange
                  }
                  onChange={(value) =>
                    onChangeAgeRange(value ?? ANY_AGE_RANGE_LABEL)
                  }
                  colors={{
                    track: colors.primary,
                    trackRemaining: colors.border,
                    thumb: colors.primary,
                    label: colors.textPrimary,
                    labelMuted: colors.textSecondary,
                  }}
                />
              </View>

              <View style={styles.circleField}>
                <Text style={styles.circleFieldLabel}>
                  {t(homeStrings.groupSize)}
                </Text>
                <FilterSelect
                  placeholder={t(homeStrings.selectSize)}
                  options={GROUP_SIZES.map(String)}
                  selected={String(maxMembers)}
                  onChange={(value) =>
                    onChangeMaxMembers(value ? Number(value) : maxMembers)
                  }
                  labelFor={(value) =>
                    t(homeStrings.peopleOption, { count: Number(value) })
                  }
                  colors={colors}
                  styles={styles}
                />
              </View>

              <View style={styles.circleField}>
                <Text style={styles.circleFieldLabel}>
                  {t(homeStrings.durationQuestion)}
                </Text>
                <FilterSelect
                  placeholder={t(homeStrings.selectDuration)}
                  options={GROUP_DURATIONS.map(String)}
                  selected={String(durationMinutes)}
                  onChange={(value) =>
                    onChangeDuration(value ? Number(value) : durationMinutes)
                  }
                  labelFor={(value) =>
                    t(homeStrings.hours, { count: Number(value) / 60 })
                  }
                  colors={colors}
                  styles={styles}
                />
              </View>

              {/* How close someone must be to JOIN. This is the host's rule,
                  not a filter: people outside it neither see the group nor can
                  accept an invitation to it. */}
              <View style={styles.circleField}>
                <Text style={styles.circleFieldLabel}>
                  {t(homeStrings.groupRangeQuestion)}
                </Text>
                <Text style={styles.groupRangeHint}>
                  {t(homeStrings.groupRangeHint, {
                    distance: formatRadiusM(radiusM),
                  })}
                </Text>
                <NearbyRadiusSlider
                  valueM={radiusM}
                  onCommit={onChangeRadius}
                  colors={{
                    track: colors.primary,
                    trackRemaining: colors.border,
                    thumb: colors.primary,
                    label: colors.textPrimary,
                    labelMuted: colors.textSecondary,
                  }}
                />
              </View>

              <View style={styles.circleField}>
                <Text style={styles.circleFieldLabel}>
                  {t(homeStrings.optionalNote)}
                </Text>
                <TextInput
                  value={prompt}
                  onChangeText={onChangePrompt}
                  placeholder={t(homeStrings.notePlaceholder)}
                  placeholderTextColor={colors.textMuted}
                  multiline
                  maxLength={120}
                  style={[
                    styles.groupPromptInput,
                    { color: colors.textPrimary, borderColor: colors.border },
                  ]}
                />
              </View>
            </ScrollView>

            <View style={styles.cuePromptActions}>
              <Pressable
                disabled={creating}
                onPress={onCancel}
                style={({ pressed }) => [
                  styles.cuePromptButton,
                  styles.cuePromptCancelButton,
                  pressed && styles.cuePromptButtonPressed,
                ]}
              >
                <Text style={styles.cuePromptCancelText}>
                  {t(homeStrings.cancel)}
                </Text>
              </Pressable>

              <Pressable
                disabled={creating || !canCreate}
                onPress={onCreate}
                style={({ pressed }) => [
                  styles.cuePromptButton,
                  styles.cuePromptOkButton,
                  (creating || !canCreate) && styles.buttonDisabled,
                  pressed && styles.cuePromptButtonPressed,
                ]}
              >
                {creating ? (
                  <ActivityIndicator color={colors.white} />
                ) : (
                  <Text style={styles.cuePromptOkText}>
                    {t(homeStrings.create)}
                  </Text>
                )}
              </Pressable>
            </View>
          </View>
        </KeyboardAvoidingContainer>
      </View>
    </Modal>
  );
}

function JoinGroupModal({
  group,
  joining,
  onCancel,
  onJoin,
  colors,
  styles,
}: {
  group: SmallGroup | null;
  joining: boolean;
  onCancel: () => void;
  /** `true` = Cue me. The intent rides on the join; see `GroupService.join`. */
  onJoin: (allowMemberCues: boolean) => void;
  colors: HomeColors;
  styles: HomeStyles;
}) {
  const { t } = useTranslation();
  return (
    <Modal
      visible={Boolean(group)}
      transparent
      animationType="fade"
      onRequestClose={onCancel}
    >
      <View style={styles.cuePromptRoot}>
        <BlurredBackdrop
          accessibilityLabel={t(homeStrings.cancelJoinA11y)}
          disabled={joining}
          onPress={onCancel}
          style={styles.cuePromptBackdrop}
        />

        <View style={styles.createGroupCard}>
          <Text style={styles.createGroupTitle}>
            {t(homeStrings.joinTitle)}
          </Text>
          <Text style={styles.createGroupDescription}>
            {group
              ? t(homeStrings.joinSummary, {
                  name: group.name,
                  count: group.memberCount,
                  timeLeft: formatCompactDuration(group.minutesLeft),
                })
              : ""}
          </Text>
          <Text style={styles.circleJoinHint}>
            {t(homeStrings.leaveAnytime)}
          </Text>
          <Text style={styles.createGroupDescription}>
            {t(homeStrings.joinCueHint)}
          </Text>

          {/*
            Two joins, not a join plus a checkbox.
            
            The permission is a decision about who may reach you, and a checkbox
            beside a Join button is the shape people tap past — it inherits
            whatever it was last left at, and its default silently becomes the
            answer for everybody who did not read it. Two labelled buttons make
            the choice the act of joining, so there is no default to inherit and
            no state to be wrong about.
            
            "Join only" is listed second and styled as the quieter of the two,
            not because it is the lesser choice but because it is the one that
            grants nothing: a mis-tap on it costs a permission that can be
            switched on again from the roster in one tap, while a mis-tap the
            other way hands strangers a channel the person did not mean to open.
          */}
          <View style={styles.joinPromptActions}>
            <Pressable
              disabled={joining}
              accessibilityRole="button"
              onPress={() => onJoin(true)}
              style={({ pressed }) => [
                styles.joinPromptButton,
                styles.cuePromptOkButton,
                joining && styles.buttonDisabled,
                pressed && styles.cuePromptButtonPressed,
              ]}
            >
              {joining ? (
                <ActivityIndicator color={colors.white} />
              ) : (
                <Text style={styles.cuePromptOkText}>
                  {t(homeStrings.joinAndCue)}
                </Text>
              )}
            </Pressable>

            <Pressable
              disabled={joining}
              accessibilityRole="button"
              onPress={() => onJoin(false)}
              style={({ pressed }) => [
                styles.joinPromptButton,
                styles.cuePromptCancelButton,
                joining && styles.buttonDisabled,
                pressed && styles.cuePromptButtonPressed,
              ]}
            >
              <Text style={styles.cuePromptCancelText}>
                {t(homeStrings.joinOnly)}
              </Text>
            </Pressable>

            <Pressable
              disabled={joining}
              accessibilityRole="button"
              onPress={onCancel}
              style={({ pressed }) => [
                styles.joinPromptButton,
                pressed && styles.cuePromptButtonPressed,
              ]}
            >
              <Text style={styles.cuePromptCancelText}>
                {t(homeStrings.cancel)}
              </Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
}

/**
 * Stands in for the people list when the location grant is approximate.
 *
 * Not an error and not a refusal — the rest of opncue runs on approximate
 * location. This is the one feature it cannot support, so it says which feature
 * and why rather than demanding a permission for its own sake.
 */
function PreciseRequiredPeople({
  compact = false,
  onAllowPrecise,
  colors,
  styles,
}: {
  compact?: boolean;
  onAllowPrecise: () => void;
  colors: HomeColors;
  styles: HomeStyles;
}) {
  const { t } = useTranslation();
  return (
    <View style={[styles.empty, compact && styles.emptyInPanel]}>
      <View style={styles.emptyGraphic}>
        <View style={styles.emptyOuterRing} />

        <View style={styles.emptyInnerRing} />

        <View style={styles.emptyIcon}>
          <Ionicons name="location-outline" size={31} color={colors.primary} />
        </View>
      </View>

      <Text style={styles.emptyTitle}>
        {t(homeStrings.peoplePreciseTitle)}
      </Text>

      <Text style={styles.emptyDescription}>
        {t(homeStrings.peoplePreciseBody)}
      </Text>

      <Pressable
        accessibilityRole="button"
        accessibilityLabel={t(homeStrings.preciseActionA11y)}
        onPress={onAllowPrecise}
        style={({ pressed }) => [
          styles.emptyShareButton,
          pressed && styles.emptyShareButtonPressed,
        ]}
      >
        <Ionicons name="location-outline" size={17} color={colors.primaryStrong} />

        <Text style={styles.emptyShareButtonText}>
          {t(homeStrings.preciseAction)}
        </Text>
      </Pressable>
    </View>
  );
}

function EmptyVenue({
  compact = false,
  sameSpace = false,
  onShare,
  colors,
  styles,
}: {
  compact?: boolean;
  /**
   * Whether the empty list was the SPACE's answer or the radius's. Only the
   * waiting line changes: "nearby" is false in Same Space — the list never
   * looked past the venue — and the invitation above it is right either way.
   */
  sameSpace?: boolean;
  onShare: () => void;
  colors: HomeColors;
  styles: HomeStyles;
}) {
  const { t } = useTranslation();
  return (
    <View style={[styles.empty, compact && styles.emptyInPanel]}>
      <View style={styles.emptyGraphic}>
        <View style={styles.emptyOuterRing} />

        <View style={styles.emptyInnerRing} />

        <View style={styles.emptyIcon}>
          <Ionicons name="people-outline" size={31} color={colors.primary} />
        </View>
      </View>

      <Text style={styles.emptyTitle}>{t(homeStrings.peopleEmptyTitle)}</Text>

      <Text style={styles.emptyDescription}>
        {t(homeStrings.peopleEmptyBody)}
      </Text>

      <Pressable
        accessibilityRole="button"
        accessibilityLabel={t(homeStrings.shareButton)}
        onPress={onShare}
        style={({ pressed }) => [
          styles.emptyShareButton,
          pressed && styles.emptyShareButtonPressed,
        ]}
      >
        <Ionicons
          name="paper-plane-outline"
          size={17}
          color={colors.primaryStrong}
        />

        <Text style={styles.emptyShareButtonText}>
          {t(homeStrings.shareButton)}
        </Text>
      </Pressable>

      {/* The reason the screen looks like this. Without it the invitation above
          arrives unexplained — somebody who expected to see people is told to
          bring one, with nothing saying that the app is still looking. */}
      <View style={styles.waitingBadge}>
        <View style={styles.waitingDot} />

        <Text style={styles.waitingText}>
          {t(
            sameSpace ? homeStrings.peopleWaitingHere : homeStrings.peopleWaiting,
          )}
        </Text>
      </View>
    </View>
  );
}

function EmptyFilteredPeople({
  onClear,
  colors,
  styles,
}: {
  onClear: () => void;
  colors: HomeColors;
  styles: HomeStyles;
}) {
  const { t } = useTranslation();
  return (
    <View style={[styles.empty, styles.emptyInPanel]}>
      <View style={styles.emptyGraphic}>
        <View style={styles.emptyOuterRing} />
        <View style={styles.emptyInnerRing} />
        <View style={styles.emptyIcon}>
          <Ionicons name="filter-outline" size={31} color={colors.primary} />
        </View>
      </View>

      <Text style={styles.emptyTitle}>{t(homeStrings.noPeopleMatch)}</Text>

      <Text style={styles.emptyDescription}>
        {t(homeStrings.noPeopleMatchBody)}
      </Text>

      <Pressable
        accessibilityRole="button"
        accessibilityLabel={t(homeStrings.clearPeopleA11y)}
        onPress={onClear}
        style={({ pressed }) => [
          styles.emptyShareButton,
          pressed && styles.emptyShareButtonPressed,
        ]}
      >
        <Ionicons
          name="close-circle-outline"
          size={17}
          color={colors.primaryStrong}
        />
        <Text style={styles.emptyShareButtonText}>
          {t(homeStrings.clearFilters)}
        </Text>
      </Pressable>
    </View>
  );
}

function createStyles(colors: HomeColors, isDark: boolean) {
  return StyleSheet.create({
    safe: {
      flex: 1,
      backgroundColor: colors.screen,
    },

    topBar: {
      minHeight: 69,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: colors.divider,
      backgroundColor: colors.topBar,
    },

    topBarInner: {
      width: "100%",
      maxWidth: CONTENT_MAX_WIDTH,
      minHeight: 69,
      alignSelf: "center",
      paddingHorizontal: CONTENT_SIDE_PADDING,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
    },

    topBarTitle: {
      flexShrink: 1,
      color: colors.primary,
      fontFamily: FONT_FAMILY,
      fontSize: 13,
      lineHeight: 18,
      fontWeight: "800",
      letterSpacing: 1.2,
    },

    topBarControls: {
      flex: 1,
      flexDirection: "row",
      alignItems: "center",
      gap: 10,
    },

    topBarVisibilityButton: {
      flex: 1,
      minHeight: 44,
      paddingHorizontal: 10,
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
      borderRadius: 18,
      borderWidth: 1,
      borderColor: colors.primaryBorder,
      backgroundColor: colors.primarySurface,
    },

    topBarVisibilityButtonActive: {
      borderColor: colors.greenBorder,
      backgroundColor: colors.greenSoft,
    },

    topBarVisibilityButtonPressed: {
      opacity: 0.78,
    },

    topBarVisibilityCopy: {
      flex: 1,
      minWidth: 0,
    },

    topBarVisibilityLabel: {
      color: colors.danger,
      fontFamily: FONT_FAMILY,
      fontSize: 12,
      lineHeight: 16,
      fontWeight: "800",
    },

    topBarVisibilityLabelActive: {
      color: colors.green,
    },

    topBarVisibilityHelper: {
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 9,
      lineHeight: 12,
      fontWeight: "600",
    },

    topBarPeopleCount: {
      minHeight: 44,
      maxWidth: 118,
      paddingHorizontal: 10,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      borderRadius: 18,
      borderWidth: 1,
      borderColor: colors.primaryBorder,
      backgroundColor: colors.primarySurface,
    },

    topBarPeopleCountText: {
      flexShrink: 1,
      color: colors.primary,
      fontFamily: FONT_FAMILY,
      fontSize: 11,
      lineHeight: 15,
      fontWeight: "800",
    },

    list: {
      width: "100%",
      maxWidth: CONTENT_MAX_WIDTH,
      alignSelf: "center",
      paddingHorizontal: CONTENT_SIDE_PADDING,
      paddingTop: 24,
      paddingBottom: 55,
      gap: 13,
    },

    // Grows the content container to the full scroller height so a lone item
    // has something to centre within — a FlatList cell cannot flex on its own.
    listCentered: {
      flexGrow: 1,
      justifyContent: "center",
    },

    listFrame: {
      flex: 1,
    },

    listScroller: {
      flex: 1,
    },

    peoplePanelFrame: {
      flex: 1,
      alignSelf: "center",
      marginBottom: HOME_FOOTER_GAP,
      overflow: Platform.OS === "web" ? "hidden" : "visible",
      borderRadius: Platform.OS === "web" ? 20 : 0,
      borderWidth: Platform.OS === "web" ? 1 : 0,
      borderColor: Platform.OS === "web" ? colors.border : "transparent",
      backgroundColor: Platform.OS === "web" ? colors.surface : "transparent",
      shadowColor: colors.shadow,
      shadowOpacity: Platform.OS === "web" ? (isDark ? 0.22 : 0.07) : 0,
      shadowRadius: Platform.OS === "web" ? 14 : 0,
      shadowOffset: {
        width: 0,
        height: Platform.OS === "web" ? 7 : 0,
      },
      elevation: Platform.OS === "web" ? 2 : 0,
    },

    peoplePanelList: {
      flexGrow: 1,
      paddingTop: Platform.OS === "web" ? 24 : 20,
      paddingHorizontal: Platform.OS === "web" ? 18 : 14,
      paddingBottom: 26,
      gap: 0,
    },

    header: {
      gap: 22,
      marginBottom: 4,
    },

    peoplePanelHeader: {
      gap: 0,
      marginBottom: 0,
    },

    /**
     * The right-hand side when there is no named venue: the people count and
     * the live badge together, where the badge used to sit alone.
     *
     * `flexShrink` so a long count cannot push the badge off the row —
     * `topBarInner` is `space-between` and the left side is a title that
     * already shrinks.
     */
    topBarUnplacedMeta: {
      flexShrink: 1,
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
    },

    liveBadge: {
      marginTop: 2,
      minHeight: 30,
      paddingHorizontal: 10,
      flexDirection: "row",
      alignItems: "center",
      gap: 6,
      borderRadius: 15,
      borderWidth: 1,
      borderColor: colors.greenBorder,
      backgroundColor: colors.greenSoft,
    },

    liveDot: {
      width: 7,
      height: 7,
      borderRadius: 4,
      backgroundColor: colors.green,
    },

    liveText: {
      color: colors.green,
      fontFamily: FONT_FAMILY,
      fontSize: 12,
      fontWeight: "700",
    },

    venueCard: {
      position: "relative",
      overflow: "hidden",
      paddingBottom: 16,
      gap: 12,
      backgroundColor: "transparent",
    },

    venueCardTop: {
      flexDirection: "row",
      alignItems: "center",
      gap: 14,
    },

    venueIcon: {
      width: 48,
      height: 48,
      borderRadius: 24,
      alignItems: "center",
      justifyContent: "center",
      borderWidth: 1,
      borderColor: colors.primaryBorder,
      backgroundColor: colors.primarySurface,
    },

    venueHeading: {
      flex: 1,
    },

    venueLabel: {
      marginBottom: 4,
      color: colors.textMuted,
      fontFamily: FONT_FAMILY,
      fontSize: 11,
      fontWeight: "800",
      letterSpacing: 1.5,
    },

    venueTitleRow: {
      flexDirection: "row",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 12,
    },

    venueName: {
      flexShrink: 1,
      color: colors.textPrimary,
      fontFamily: FONT_FAMILY,
      fontSize: 20,
      lineHeight: 25,
      fontWeight: "700",
    },

    editLocationButton: {
      minHeight: 32,
      paddingHorizontal: 11,
      flexDirection: "row",
      alignItems: "center",
      gap: 6,
      borderRadius: 16,
      borderWidth: 1,
      borderColor: colors.primaryBorder,
      backgroundColor: colors.primarySurface,
    },

    editLocationButtonPressed: {
      opacity: 0.8,
      transform: [{ scale: 0.98 }],
    },

    editLocationText: {
      color: colors.primary,
      fontFamily: FONT_FAMILY,
      fontSize: 12,
      fontWeight: "700",
    },

    panelTabs: {
      paddingVertical: 10,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 7,
    },

    panelTabsStickyHeader: {
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: colors.divider,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: colors.divider,
      backgroundColor: Platform.OS === "web" ? colors.surface : colors.screen,
      zIndex: 10,
      elevation: 0,
    },

    panelCompositeButton: {
      flex: 1.22,
      minHeight: 38,
      paddingHorizontal: 3,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 12,
      borderWidth: 1,
      borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(16,45,81,0.10)",
      backgroundColor: isDark
        ? "rgba(255,255,255,0.025)"
        : "rgba(255,255,255,0.44)",
    },

    panelTab: {
      flex: 1,
      minHeight: 38,
      paddingHorizontal: 12,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 12,
      borderWidth: 1,
      borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(16,45,81,0.10)",
      backgroundColor: isDark
        ? "rgba(255,255,255,0.025)"
        : "rgba(255,255,255,0.44)",
    },

    panelTabActive: {
      borderColor: colors.primaryBorder,
      backgroundColor: colors.primarySurface,
    },

    panelTabGroups: {
      flex: 0.82,
      paddingHorizontal: 6,
    },

    panelTabPressed: {
      opacity: 0.78,
    },

    panelTabText: {
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 13,
      lineHeight: 17,
      fontWeight: "800",
      textAlign: "center",
    },

    panelTabGroupsText: {
      fontSize: 12,
      lineHeight: 16,
    },

    panelTabTextActive: {
      color: colors.primary,
    },

    panelInnerSeparator: {
      color: colors.primary,
      fontFamily: FONT_FAMILY,
      fontSize: 16,
      lineHeight: 20,
      fontWeight: "800",
      opacity: 0.75,
    },

    panelCompositeSegment: {
      flex: 1,
      minHeight: 34,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 9,
    },

    panelCompositeFilter: {
      minHeight: 32,
      minWidth: 70,
      paddingHorizontal: 8,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 5,
      borderRadius: 9,
    },

    panelCompositeFilterActive: {
      backgroundColor: colors.primary,
    },

    inlineFilterText: {
      color: colors.primary,
      fontFamily: FONT_FAMILY,
      fontSize: 11,
      lineHeight: 14,
      fontWeight: "800",
    },

    inlineFilterTextActive: {
      color: colors.white,
    },

    peopleFilterBar: {
      paddingTop: 10,
      paddingBottom: 12,
      flexDirection: "row",
      alignItems: "center",
      gap: 10,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: colors.divider,
    },

    peopleFilterButton: {
      minHeight: 34,
      paddingHorizontal: 12,
      flexDirection: "row",
      alignItems: "center",
      gap: 6,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: colors.primaryBorder,
      backgroundColor: colors.primarySurface,
    },

    peopleFilterButtonActive: {
      borderColor: colors.primary,
      backgroundColor: colors.primary,
    },

    peopleFilterButtonPressed: {
      opacity: 0.78,
    },

    peopleFilterButtonText: {
      color: colors.primary,
      fontFamily: FONT_FAMILY,
      fontSize: 12,
      lineHeight: 16,
      fontWeight: "800",
    },

    peopleFilterButtonTextActive: {
      color: colors.white,
    },

    peopleFilterSummary: {
      flex: 1,
      color: colors.textMuted,
      fontFamily: FONT_FAMILY,
      fontSize: 12,
      lineHeight: 16,
      fontWeight: "700",
    },

    peopleFilterClear: {
      minHeight: 32,
      paddingHorizontal: 8,
      alignItems: "center",
      justifyContent: "center",
    },

    peopleFilterClearText: {
      color: colors.primary,
      fontFamily: FONT_FAMILY,
      fontSize: 12,
      lineHeight: 16,
      fontWeight: "800",
    },

    peoplePanelRowDivider: {
      height: StyleSheet.hairlineWidth,
      backgroundColor: colors.divider,
    },






    shareCard: {
      padding: 18,
      gap: 16,
      borderRadius: 20,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
      shadowColor: colors.shadow,
      shadowOpacity: isDark ? 0.22 : 0.07,
      shadowRadius: 14,
      shadowOffset: {
        width: 0,
        height: 7,
      },
      elevation: 2,
    },

    shareCardTop: {
      flexDirection: "row",
      alignItems: "flex-start",
      gap: 13,
    },

    shareIconWrap: {
      width: 44,
      height: 44,
      borderRadius: 13,
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: colors.primarySoft,
    },

    shareCopy: {
      flex: 1,
    },

    shareTitle: {
      color: colors.textStrong,
      fontFamily: FONT_FAMILY,
      fontSize: 16,
      lineHeight: 21,
      fontWeight: "700",
    },

    shareDescription: {
      marginTop: 4,
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 12,
      lineHeight: 18,
    },

    shareButton: {
      minHeight: 48,
      paddingHorizontal: 16,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      borderRadius: 13,
      backgroundColor: BRAND_BLUE,
      shadowColor: BRAND_BLUE_SHADOW,
      shadowOpacity: 0.18,
      shadowRadius: 9,
      shadowOffset: {
        width: 0,
        height: 5,
      },
      elevation: 3,
    },

    shareButtonPressed: {
      opacity: 0.82,
      transform: [{ scale: 0.99 }],
    },

    shareButtonText: {
      color: colors.white,
      fontFamily: FONT_FAMILY,
      fontSize: 14,
      fontWeight: "700",
    },

    nearbyRowWrap: {
      overflow: "hidden",
      borderRadius: 0,
    },

    nearbyRowInPanel: {
      overflow: "visible",
      borderRadius: 0,
      shadowOpacity: 0,
      elevation: 0,
    },

    // Deliberately not a card. This is the whole screen's state, not one item
    // among several, so it sits on the page rather than in a surface of its own.
    noVenueState: {
      paddingVertical: 8,
      alignItems: "center",
      gap: 22,
    },

    noVenueGraphic: {
      width: 124,
      height: 124,
      alignItems: "center",
      justifyContent: "center",
    },

    noVenueOuterRing: {
      position: "absolute",
      width: 124,
      height: 124,
      borderRadius: 62,
      borderWidth: 1,
      borderColor: isDark ? "rgba(110,168,255,0.22)" : "rgba(58,116,207,0.12)",
    },

    noVenueInnerRing: {
      position: "absolute",
      width: 92,
      height: 92,
      borderRadius: 46,
      backgroundColor: colors.primarySoft,
    },

    noVenueIcon: {
      width: 62,
      height: 62,
      borderRadius: 31,
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: BRAND_BLUE,
      shadowColor: isDark ? colors.shadow : BRAND_BLUE_SHADOW,
      shadowOpacity: 0.25,
      shadowRadius: 13,
      shadowOffset: {
        width: 0,
        height: 7,
      },
      elevation: 6,
    },

    noVenueCopy: {
      alignItems: "center",
      gap: 9,
      // Without the card's own padding the text would otherwise run the full
      // content width, which is a long measure for two centred lines.
      maxWidth: 360,
    },

    noVenueTitle: {
      color: colors.textPrimary,
      fontFamily: FONT_FAMILY,
      fontSize: 23,
      lineHeight: 29,
      fontWeight: "700",
      letterSpacing: -0.4,
      textAlign: "center",
    },

    noVenueDescription: {
      maxWidth: 330,
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 15,
      lineHeight: 23,
      textAlign: "center",
    },

    noVenueActions: {
      width: "100%",
      gap: 12,
    },

    primaryButton: {
      minHeight: 59,
      paddingHorizontal: 18,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 10,
      borderRadius: 14,
      backgroundColor: BRAND_BLUE,
      shadowColor: BRAND_BLUE_SHADOW,
      shadowOpacity: 0.22,
      shadowRadius: 12,
      shadowOffset: {
        width: 0,
        height: 7,
      },
      elevation: 5,
    },

    primaryButtonPressed: {
      opacity: 0.82,
      transform: [{ scale: 0.99 }],
    },

    primaryButtonText: {
      color: colors.white,
      fontFamily: FONT_FAMILY,
      fontSize: 16,
      fontWeight: "700",
    },

    secondaryButton: {
      minHeight: 56,
      paddingHorizontal: 18,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 9,
      borderRadius: 14,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
    },

    secondaryButtonPressed: {
      backgroundColor: colors.surfacePressed,
    },

    secondaryButtonText: {
      color: colors.primaryStrong,
      fontFamily: FONT_FAMILY,
      fontSize: 15,
      fontWeight: "700",
    },

    buttonDisabled: {
      opacity: 0.6,
    },

    empty: {
      marginTop: 10,
      paddingVertical: 40,
      paddingHorizontal: Platform.OS === "web" ? 22 : 0,
      alignItems: "center",
      borderRadius: Platform.OS === "web" ? 23 : 0,
      borderWidth: Platform.OS === "web" ? 1 : 0,
      borderColor: colors.border,
      backgroundColor: Platform.OS === "web" ? colors.surface : "transparent",
    },

    emptyInPanel: {
      flexGrow: Platform.OS === "web" ? 1 : 0,
      marginTop: 0,
      justifyContent: Platform.OS === "web" ? "center" : "flex-start",
      borderWidth: 0,
      borderRadius: 0,
      backgroundColor: "transparent",
    },

    circlePanel: {
      paddingTop: 16,
      gap: 14,
    },

    circleHero: {
      paddingHorizontal: 2,
    },

    circleHeroDescription: {
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 13,
      lineHeight: 19,
      fontWeight: "500",
    },

    circleHeroActions: {
      flexDirection: "row",
      gap: 10,
    },

    circlePrimaryButton: {
      flex: 1,
      minHeight: 44,
      paddingHorizontal: 12,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 7,
      borderRadius: 14,
      backgroundColor: BRAND_BLUE,
    },

    circlePrimaryButtonText: {
      color: colors.white,
      fontFamily: FONT_FAMILY,
      fontSize: 13,
      fontWeight: "800",
    },

    circleSecondaryButton: {
      flex: 1,
      minHeight: 44,
      paddingHorizontal: 12,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 7,
      borderRadius: 14,
      borderWidth: 1,
      borderColor: colors.primaryBorder,
      backgroundColor: colors.primarySurface,
    },

    circleSecondaryButtonText: {
      color: colors.primary,
      fontFamily: FONT_FAMILY,
      fontSize: 13,
      fontWeight: "800",
    },

    circleButtonPressed: {
      opacity: 0.82,
      transform: [{ scale: 0.99 }],
    },

    circleLoading: {
      minHeight: 140,
      alignItems: "center",
      justifyContent: "center",
    },

    // Roughly the height of the rows it stands in for, so the list does not
    // jump when they arrive.
    peopleLoading: {
      minHeight: 160,
      justifyContent: "center",
    },

    // Deliberately short, unlike `peopleLoading`. This one sits below rows that
    // are already on screen, so it only has to be visible — reserving row-sized
    // space for it would drag the list down every time the user reached the
    // bottom.
    peoplePagingFooter: {
      paddingVertical: 20,
    },

    circleList: {
      gap: 0,
    },

    circleCountText: {
      color: colors.textMuted,
      fontFamily: FONT_FAMILY,
      fontSize: 11,
      fontWeight: "800",
      letterSpacing: 0.8,
      textTransform: "uppercase",
    },

    circleCard: {
      minHeight: 88,
      paddingHorizontal: 10,
      paddingVertical: 10,
      flexDirection: "row",
      alignItems: "center",
      gap: 12,
      borderRadius: 12,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: colors.border,
      backgroundColor: "transparent",
    },

    circleCardHovered: {
      backgroundColor: colors.surfaceMuted,
    },

    circleCardPressed: {
      backgroundColor: colors.surfacePressed,
    },

    circleCardDisabled: {
      opacity: 0.58,
    },

    circleCardTop: {
      flexDirection: "row",
      alignItems: "center",
      gap: 12,
    },

    circleGroupIcon: {
      width: 50,
      height: 50,
      borderRadius: 17,
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: colors.primarySoft,
    },

    circleCardTitleWrap: {
      flex: 1,
      minWidth: 0,
      gap: 3,
    },

    circleCardTitle: {
      color: colors.textPrimary,
      fontFamily: FONT_FAMILY,
      fontSize: 15,
      lineHeight: 20,
      fontWeight: "800",
    },

    circleCardMeta: {
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 12,
      lineHeight: 16,
      fontWeight: "600",
    },

    circleRowMeta: {
      minWidth: 56,
      alignItems: "flex-end",
      gap: 5,
    },

    circleRowStatus: {
      color: colors.primaryStrong,
      fontFamily: FONT_FAMILY,
      fontSize: 12,
      lineHeight: 16,
      fontWeight: "800",
    },

    circleRowStatusMuted: {
      color: colors.textMuted,
    },

    circleJoinButton: {
      minHeight: 36,
      minWidth: 70,
      paddingHorizontal: 12,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 12,
      backgroundColor: colors.primary,
    },

    circleJoinButtonText: {
      color: colors.white,
      fontFamily: FONT_FAMILY,
      fontSize: 12,
      fontWeight: "800",
    },

    groupPrompt: {
      color: colors.textBody,
      fontFamily: FONT_FAMILY,
      fontSize: 13,
      lineHeight: 19,
    },

    circleMembers: {
      color: colors.textMuted,
      fontFamily: FONT_FAMILY,
      fontSize: 12,
      lineHeight: 17,
      fontWeight: "600",
    },

    circleEmpty: {
      minHeight: 150,
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      paddingHorizontal: 12,
    },

    circleEmptyTitle: {
      color: colors.textPrimary,
      fontFamily: FONT_FAMILY,
      fontSize: 17,
      lineHeight: 22,
      fontWeight: "800",
      textAlign: "center",
    },

    circleEmptyDescription: {
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 13,
      lineHeight: 20,
      textAlign: "center",
    },

    createGroupCard: {
      width: "100%",
      maxWidth: 470,
      maxHeight: "88%",
      padding: 18,
      gap: 14,
      borderRadius: 20,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
      shadowColor: colors.shadow,
      shadowOpacity: isDark ? 0.3 : 0.18,
      shadowRadius: 24,
      shadowOffset: {
        width: 0,
        height: 12,
      },
      elevation: 12,
    },

    peopleFilterSheet: {
      width: "100%",
      maxWidth: 470,
      maxHeight: "88%",
      padding: 18,
      gap: 14,
      borderRadius: 20,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
      shadowColor: colors.shadow,
      shadowOpacity: isDark ? 0.3 : 0.18,
      shadowRadius: 24,
      shadowOffset: {
        width: 0,
        height: 12,
      },
      elevation: 12,
    },

    peopleFilterSheetHeader: {
      flexDirection: "row",
      alignItems: "flex-start",
      gap: 13,
    },

    peopleFilterSection: {
      gap: 8,
      paddingVertical: 10,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: colors.divider,
    },

    peopleFilterLocked: {
      minHeight: 36,
      paddingHorizontal: 10,
      paddingVertical: 8,
      flexDirection: "row",
      alignItems: "center",
      gap: 7,
      borderRadius: 11,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surfaceMuted,
    },

    peopleFilterLockedText: {
      flex: 1,
      color: colors.textMuted,
      fontFamily: FONT_FAMILY,
      fontSize: 12,
      lineHeight: 17,
      fontWeight: "600",
    },

    peopleFilterPickerShell: {
      minHeight: 44,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 8,
      paddingHorizontal: 14,
      borderRadius: 11,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surfaceMuted,
    },

    peopleFilterSelectText: {
      flex: 1,
      fontFamily: FONT_FAMILY,
      fontSize: 14,
    },

    filterSelectBackdrop: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(8, 15, 26, 0.5)",
    },

    filterSelectCenter: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      paddingHorizontal: 28,
    },

    filterSelectSheet: {
      width: "100%",
      maxWidth: 360,
      maxHeight: "60%",
      borderRadius: 16,
      borderWidth: 1,
      overflow: "hidden",
    },

    filterSelectOption: {
      minHeight: 48,
      paddingHorizontal: 16,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 8,
      borderBottomWidth: StyleSheet.hairlineWidth,
    },

    filterSelectOptionStacked: {
      paddingVertical: 10,
    },

    filterSelectOptionCopy: {
      flex: 1,
      gap: 2,
    },

    filterSelectOptionText: {
      flex: 1,
      fontFamily: FONT_FAMILY,
      fontSize: 15,
    },

    filterSelectOptionDescription: {
      fontFamily: FONT_FAMILY,
      fontSize: 13,
      lineHeight: 17,
    },

    createGroupScroll: {
      gap: 14,
      paddingBottom: 2,
    },

    createGroupTitle: {
      color: colors.textPrimary,
      fontFamily: FONT_FAMILY,
      fontSize: 20,
      lineHeight: 25,
      fontWeight: "800",
    },

    createGroupDescription: {
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 13,
      lineHeight: 19,
      fontWeight: "500",
    },

    circleField: {
      gap: 8,
    },

    circleFieldLabel: {
      color: colors.textStrong,
      fontFamily: FONT_FAMILY,
      fontSize: 12,
      lineHeight: 16,
      fontWeight: "800",
    },

    groupRangeHint: {
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 12,
      lineHeight: 16,
      marginTop: 2,
    },

    circleOptions: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: 8,
    },

    circleOption: {
      minHeight: 34,
      paddingHorizontal: 10,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 12,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surfaceMuted,
    },

    circleOptionActive: {
      borderColor: colors.primaryBorder,
      backgroundColor: colors.primarySurface,
    },

    circleOptionText: {
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 12,
      fontWeight: "700",
    },

    circleOptionTextActive: {
      color: colors.primary,
    },

    groupPromptInput: {
      minHeight: 74,
      paddingHorizontal: 12,
      paddingVertical: 10,
      borderRadius: 14,
      borderWidth: 1,
      fontFamily: FONT_FAMILY,
      fontSize: 13,
      lineHeight: 18,
      textAlignVertical: "top",
      backgroundColor: colors.surfaceMuted,
    },

    groupTextInput: {
      minHeight: 44,
      paddingHorizontal: 12,
      borderRadius: 14,
      borderWidth: 1,
      fontFamily: FONT_FAMILY,
      fontSize: 14,
      lineHeight: 19,
      backgroundColor: colors.surfaceMuted,
    },

    circleJoinHint: {
      color: colors.textBody,
      fontFamily: FONT_FAMILY,
      fontSize: 14,
      lineHeight: 20,
      fontWeight: "600",
    },

    emptyGraphic: {
      width: 116,
      height: 116,
      marginBottom: 18,
      alignItems: "center",
      justifyContent: "center",
    },

    emptyOuterRing: {
      position: "absolute",
      width: 116,
      height: 116,
      borderRadius: 58,
      borderWidth: 1,
      borderColor: isDark ? "rgba(110,168,255,0.21)" : "rgba(60,117,210,0.11)",
    },

    emptyInnerRing: {
      position: "absolute",
      width: 84,
      height: 84,
      borderRadius: 42,
      backgroundColor: colors.primarySoft,
    },

    emptyIcon: {
      width: 58,
      height: 58,
      borderRadius: 29,
      alignItems: "center",
      justifyContent: "center",
      borderWidth: 1,
      borderColor: colors.primaryBorder,
      backgroundColor: colors.surface,
    },

    emptyTitle: {
      color: colors.textPrimary,
      fontFamily: FONT_FAMILY,
      fontSize: 21,
      lineHeight: 27,
      fontWeight: "700",
      textAlign: "center",
    },

    emptyDescription: {
      maxWidth: 320,
      marginTop: 8,
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 14,
      lineHeight: 22,
      textAlign: "center",
    },

    emptyShareButton: {
      minHeight: 44,
      marginTop: 18,
      paddingHorizontal: 16,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 7,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: colors.primaryBorder,
      backgroundColor: colors.primarySurface,
    },

    emptyShareButtonPressed: {
      backgroundColor: colors.surfacePressed,
    },

    emptyShareButtonText: {
      color: colors.primaryStrong,
      fontFamily: FONT_FAMILY,
      fontSize: 13,
      fontWeight: "700",
    },

    waitingBadge: {
      minHeight: 34,
      marginTop: 14,
      paddingHorizontal: 13,
      flexDirection: "row",
      alignItems: "center",
      gap: 7,
      borderRadius: 17,
      backgroundColor: colors.primarySoft,
    },

    waitingDot: {
      width: 7,
      height: 7,
      borderRadius: 4,
      backgroundColor: colors.primary,
    },

    waitingText: {
      color: colors.primaryText,
      fontFamily: FONT_FAMILY,
      fontSize: 12,
      fontWeight: "600",
    },

    cuePromptRoot: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      padding: 22,
    },

    // Lifts a centred modal card clear of the keyboard. Sized like its parent
    // so the card re-centres in the space the keyboard leaves behind.
    modalKeyboardView: {
      width: "100%",
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
    },

    cuePromptBackdrop: {
      ...StyleSheet.absoluteFill,
      backgroundColor: "rgba(3, 12, 25, 0.58)",
    },

    cuePromptCard: {
      width: "100%",
      maxWidth: 420,
      padding: 18,
      gap: 16,
      borderRadius: 20,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
      shadowColor: colors.shadow,
      shadowOpacity: isDark ? 0.3 : 0.18,
      shadowRadius: 24,
      shadowOffset: {
        width: 0,
        height: 12,
      },
      elevation: 12,
    },

    cuePromptHeader: {
      flexDirection: "row",
      alignItems: "flex-start",
      gap: 13,
    },

    cuePromptIcon: {
      width: 46,
      height: 46,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 15,
      borderWidth: 1,
      borderColor: colors.primaryBorder,
      backgroundColor: colors.primarySurface,
    },

    cuePromptCopy: {
      flex: 1,
      minWidth: 0,
      gap: 5,
    },

    cuePromptTitle: {
      color: colors.textPrimary,
      fontFamily: FONT_FAMILY,
      fontSize: 19,
      lineHeight: 24,
      fontWeight: "800",
    },

    cuePromptDescription: {
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 13,
      lineHeight: 19,
      fontWeight: "500",
    },

    cuePromptToggleRow: {
      minHeight: 68,
      paddingHorizontal: 14,
      paddingVertical: 12,
      flexDirection: "row",
      alignItems: "center",
      gap: 14,
      borderRadius: 16,
      borderWidth: 1,
      borderColor: colors.greenBorder,
      backgroundColor: colors.greenSoft,
    },

    cuePromptToggleRowPressed: {
      opacity: 0.82,
    },

    cuePromptToggleCopy: {
      flex: 1,
      minWidth: 0,
      gap: 2,
    },

    cuePromptToggleTitle: {
      color: colors.green,
      fontFamily: FONT_FAMILY,
      fontSize: 14,
      lineHeight: 18,
      fontWeight: "800",
    },

    cuePromptToggleDescription: {
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 12,
      lineHeight: 17,
      fontWeight: "500",
    },

    cuePromptActions: {
      flexDirection: "row",
      gap: 10,
    },

    /**
     * The join sheet's actions, stacked rather than in a row.
     *
     * Three choices now — Cancel, Join only, Join and let members Cue me — and
     * the third has a real sentence in it. Side by side they wrap into two
     * illegible lines on a narrow handset, and the two that MATTER are the two
     * joins: putting them full-width, one under the other, makes them equally
     * easy to read and equally easy to hit. Cancel drops to a text link, which
     * is where a "do nothing" belongs.
     */
    joinPromptActions: {
      gap: 10,
    },

    joinPromptButton: {
      minHeight: 48,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 14,
    },

    cuePromptButton: {
      flex: 1,
      minHeight: 48,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 14,
    },

    cuePromptCancelButton: {
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surfaceMuted,
    },

    cuePromptOkButton: {
      backgroundColor: BRAND_BLUE,
    },

    cuePromptButtonPressed: {
      opacity: 0.82,
      transform: [{ scale: 0.99 }],
    },

    cuePromptCancelText: {
      color: colors.textStrong,
      fontFamily: FONT_FAMILY,
      fontSize: 14,
      fontWeight: "800",
    },

    cuePromptOkText: {
      color: colors.white,
      fontFamily: FONT_FAMILY,
      fontSize: 14,
      fontWeight: "800",
    },

    locationModalRoot: {
      flex: 1,
      justifyContent: "flex-end",
    },

    locationModalBackdrop: {
      ...StyleSheet.absoluteFill,
      backgroundColor: "rgba(3, 12, 25, 0.58)",
    },

    locationSheet: {
      width: "100%",
      maxWidth: 680,
      maxHeight: "88%",
      minHeight: "58%",
      alignSelf: "center",
      paddingHorizontal: 20,
      paddingTop: 10,
      paddingBottom: Platform.OS === "ios" ? 30 : 20,
      borderTopLeftRadius: 28,
      borderTopRightRadius: 28,
      borderWidth: 1,
      borderBottomWidth: 0,
      borderColor: colors.border,
      backgroundColor: colors.surface,
      shadowColor: colors.shadow,
      shadowOpacity: 0.28,
      shadowRadius: 24,
      shadowOffset: {
        width: 0,
        height: -8,
      },
      elevation: 18,
    },

    locationSheetHandle: {
      width: 42,
      height: 5,
      marginBottom: 17,
      alignSelf: "center",
      borderRadius: 3,
      backgroundColor: colors.border,
    },

    locationSheetHeader: {
      flexDirection: "row",
      alignItems: "flex-start",
      gap: 14,
    },

    locationSheetHeading: {
      flex: 1,
    },

    locationSheetEyebrow: {
      color: colors.primary,
      fontFamily: FONT_FAMILY,
      fontSize: 11,
      lineHeight: 15,
      fontWeight: "800",
      letterSpacing: 1.25,
    },

    locationSheetTitle: {
      marginTop: 5,
      color: colors.textPrimary,
      fontFamily: FONT_FAMILY,
      fontSize: 23,
      lineHeight: 29,
      fontWeight: "700",
      letterSpacing: -0.4,
    },

    locationSheetDescription: {
      marginTop: 5,
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 12,
      lineHeight: 18,
    },

    locationCloseButton: {
      width: 40,
      height: 40,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 13,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surfaceMuted,
    },

    locationCloseButtonPressed: {
      backgroundColor: colors.surfacePressed,
      transform: [{ scale: 0.97 }],
    },

    locationSearchBox: {
      minHeight: 62,
      marginTop: 20,
      paddingHorizontal: 12,
      flexDirection: "row",
      alignItems: "center",
      gap: 10,
      borderRadius: 17,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surfaceMuted,
    },

    locationSearchBoxFocused: {
      borderColor: colors.primary,
      backgroundColor: colors.surface,
    },

    locationSearchIcon: {
      width: 36,
      height: 36,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 11,
      backgroundColor: colors.primarySoft,
    },

    locationSearchInput: {
      flex: 1,
      minHeight: 58,
      paddingVertical: 0,
      color: colors.textStrong,
      fontFamily: FONT_FAMILY,
      fontSize: 14,
      lineHeight: 19,
      fontWeight: "500",
    },

    locationClearButton: {
      width: 34,
      height: 34,
      alignItems: "center",
      justifyContent: "center",
    },

    locationClearButtonPressed: {
      opacity: 0.58,
    },

    locationRadiusNotice: {
      minHeight: 40,
      marginTop: 8,
      paddingHorizontal: 4,
      flexDirection: "row",
      alignItems: "center",
      gap: 7,
    },

    locationRadiusText: {
      flex: 1,
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 11,
      lineHeight: 16,
      fontWeight: "600",
    },

    locationRefreshButton: {
      width: 34,
      height: 34,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 11,
    },

    locationRefreshButtonPressed: {
      backgroundColor: colors.surfacePressed,
    },

    locationResultsList: {
      flexGrow: 0,
      marginTop: 4,
    },

    locationResultsContent: {
      paddingBottom: 12,
      gap: 10,
    },

    locationResultsContentEmpty: {
      flexGrow: 1,
    },

    locationResultRow: {
      minHeight: 82,
      paddingHorizontal: 13,
      paddingVertical: 12,
      flexDirection: "row",
      alignItems: "center",
      gap: 12,
      borderRadius: 16,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surfaceMuted,
    },

    locationResultCurrent: {
      borderColor: colors.greenBorder,
      backgroundColor: colors.greenSoft,
    },

    locationResultPressed: {
      borderColor: colors.primaryBorder,
      backgroundColor: colors.surfacePressed,
      transform: [{ scale: 0.99 }],
    },

    locationResultDisabled: {
      opacity: 0.55,
    },

    locationResultIcon: {
      width: 44,
      height: 44,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 13,
      backgroundColor: colors.primarySoft,
    },

    locationResultCopy: {
      flex: 1,
      alignItems: "flex-start",
    },

    locationResultTitleRow: {
      width: "100%",
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
    },

    locationResultName: {
      flexShrink: 1,
      color: colors.textStrong,
      fontFamily: FONT_FAMILY,
      fontSize: 14,
      lineHeight: 19,
      fontWeight: "700",
    },

    locationResultAddress: {
      marginTop: 3,
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 11,
      lineHeight: 16,
    },

    locationResultCategory: {
      marginTop: 5,
      color: colors.primaryText,
      fontFamily: FONT_FAMILY,
      fontSize: 10,
      lineHeight: 14,
      fontWeight: "700",
    },

    locationResultAction: {
      width: 28,
      height: 38,
      alignItems: "center",
      justifyContent: "center",
    },

    locationCurrentBadge: {
      minHeight: 23,
      paddingHorizontal: 8,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 8,
      backgroundColor: colors.greenSoft,
    },

    locationCurrentBadgeText: {
      color: colors.green,
      fontFamily: FONT_FAMILY,
      fontSize: 9,
      fontWeight: "800",
    },

    locationEmptyState: {
      minHeight: 220,
      paddingHorizontal: 24,
      alignItems: "center",
      justifyContent: "center",
    },

    locationEmptyIcon: {
      width: 54,
      height: 54,
      marginBottom: 13,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 17,
      backgroundColor: colors.primarySoft,
    },

    locationEmptyTitle: {
      marginTop: 12,
      color: colors.textStrong,
      fontFamily: FONT_FAMILY,
      fontSize: 17,
      lineHeight: 22,
      fontWeight: "700",
      textAlign: "center",
    },

    locationEmptyDescription: {
      maxWidth: 320,
      marginTop: 6,
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 12,
      lineHeight: 18,
      textAlign: "center",
    },
  });
}
